/* ═══════════════════════════════════════════════════════════════
   DataTable — THE table (docs/DESIGN.md §5). Sort, filter, search,
   group, saved views, column reorder/resize/hide, per-user layout
   that follows the account across devices, an Explorer-semantics
   selection model (checkbox column, select-all, Ctrl/Shift ranges),
   a permission-gated bulk bar (Archive / Delete / Export CSV) and
   the per-table text size slider.

   NO grid/inline cell editing — killed by D3 of
   docs/FORECASTING_V2_TABLE_STANDARD_PLAN.md. A row click opens the
   record in its panel/editor; that is the one edit path.
   ═══════════════════════════════════════════════════════════════ */

import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  Filter,
  X,
  Save,
  Star,
  Trash2,
  Share2,
  Archive,
  Download,
  SlidersHorizontal,
} from 'lucide-react';
import { SearchableSelect } from './SearchableSelect';
import { ConfirmDialog } from './ui/confirm-dialog';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { FilterBuilder, type FilterField } from './FilterBuilder';
import { Icon } from './ui/Icon';
import { rem } from '../lib/rem';
import { scaledTokens } from '../lib/tokenScale';
import { allRowsSelected, nextSelectionByKey } from '../lib/listSelection';
import { csvFilename, downloadCsv, toCsv } from '../lib/csv';
import { hasAnyPermission, hasPermission } from '../../shared/permissions';
import {
  EMPTY_FILTER_STATE,
  activeRuleCount,
  fieldKind,
  isFilterGroup,
  isRuleActive,
  groupSummaryText,
  matchesFilterState,
  migrateFilterState,
  ruleValueText,
  type FilterGroup,
  type FilterRule,
  type TableFilterState,
} from './filterModel';
import { mergeGroupOptions } from './groupOptions';
import { Prefs } from '../services/prefsService';
import { FirmPrefs } from '../services/firmPrefsService';
import { DashboardViewService } from '../services/dashboardViewService';
import type { DashboardView } from '../../shared/entities';
import { appLayerProps } from './ui/app-layer';
import { RecordLink, useRecordMenu } from '../routing/RecordLink';
import { FocusScope } from '@radix-ui/react-focus-scope';
import { getUiScale, subscribeUiScale } from '../lib/uiScale';
import { resolveColumnPrefs, type FirmColumnDefault } from '../lib/columnPrefs';

// ─── Types ───

/** Clamp saved widths to each column's maxWidth (one-time additive migration
 *  on read); columns without a maxWidth and unknown keys are kept as saved. */
export function clampSavedWidths(
  saved: Record<string, number>,
  defs: { key: string; maxWidth?: number }[]
): Record<string, number> {
  const out = { ...saved };
  for (const d of defs) {
    const w = out[d.key];
    if (d.maxWidth != null && typeof w === 'number' && w > d.maxWidth) out[d.key] = d.maxWidth;
  }
  return out;
}

export interface Column<T> {
  key: string;
  label: string;
  width?: number; // initial width in px
  minWidth?: number; // minimum resize width (default 50)
  maxWidth?: number; // clamps a SAVED width on load (additive layout migration)
  sortable?: boolean; // default true
  render?: (item: T) => React.ReactNode;
  getValue?: (item: T) => string | number;
  /** Column starts hidden until the user opts in via the column picker
   *  (e.g. an optional detail column that would crowd the default view).
   *  Only applied the first time — once a `dt_hidden_${storageKey}`
   *  preference exists, the user's choice always wins. */
  defaultHidden?: boolean;
  /** Overrides the auto-detected filter UI for this column (multi-select
   *  for enum-ish values, contains for text, min/max for numbers/dates —
   *  auto-detected from the data). Pass 'none' to drop this column from
   *  the filter bar entirely (e.g. a free-text notes column). */
  filterType?: ColumnFilterType | 'none';
  /** The exact value/label pairs a multiselect filter offers for this
   *  column, instead of the distinct values scraped from the data. Use it
   *  when the stored value is an id but the user knows a name (status ids,
   *  organization ids) so the filter chip reads "Active", not "st_3". */
  filterOptions?: { value: string; label: string }[];
  /** Overrides what the FILTER PREDICATE tests for this column, when it
   *  differs from `getValue` (sort/CSV/search). Two cases: (1) a column
   *  whose `getValue` returns a sort key (e.g. a status's numeric order)
   *  rather than the id/label the filter's `anyOf` options are keyed to;
   *  (2) a MULTI-valued field (a row with several projects/assignees) —
   *  return the array and `anyOf`/`noneOf` match if ANY element is picked. */
  filterValue?: (item: T) => string | number | string[];
  /** Overrides what a CSV export writes for this column, when it differs
   *  from `getValue` (e.g. a column whose `getValue` is a sort key, not a
   *  label a human should read in the file). Never changes sort order. */
  csvValue?: (item: T) => string | number;
}

export type ColumnFilterType = 'multiselect' | 'text' | 'number-range' | 'date-range';

export type ColumnFilterValue =
  | { type: 'multiselect'; values: string[] }
  | { type: 'text'; contains: string }
  | { type: 'number-range'; min?: number; max?: number }
  | { type: 'date-range'; min?: string; max?: string };

export type ColumnFilters = Record<string, ColumnFilterValue>;

export interface GroupOption<T> {
  key: string;
  label: string;
  getValue: (item: T) => string;
}

/** A plain, modifier-free click: what Enter / the context menu's Open mean. */
const PLAIN_CLICK = { ctrlKey: false, metaKey: false, shiftKey: false } as React.MouseEvent;

export interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  keyField: keyof T & string;
  onRowClick?: (item: T) => void;
  /** Address of the record a row opens (`toAddress(...)`). When it returns a
   *  string the row's first cell is a real link: Ctrl/Cmd+click and middle
   *  click open a new tab / window, Enter opens, and on desktop the row's
   *  right-click menu offers Open / Open in new window. A plain click is
   *  unchanged (`onRowClick`); looks are unchanged. */
  rowHref?: (item: T) => string | undefined;
  groupOptions?: GroupOption<T>[];
  /** Group-by used when the user has no stored choice (default: none). */
  defaultGroupKey?: string;
  searchPlaceholder?: string;
  initialSort?: { key: string; direction: 'asc' | 'desc' };
  /** Accepts a plain string (legacy emoji/glyph callers) or a React node
   *  (e.g. a Lucide icon) — widened so callers can pass real icons instead
   *  of a text glyph, per docs/DESIGN.md §5 icon law. */
  emptyIcon?: React.ReactNode;
  emptyTitle?: string;
  emptyMessage?: string;
  toolbarExtra?: React.ReactNode;
  // Persist column widths and order to localStorage under this key
  storageKey?: string;
  /** A prior `storageKey` this table used to publish under — if the firm
   *  has a FIRM DEFAULT saved there but none yet at the current key (e.g.
   *  right after a storage-key rename), that old default still applies
   *  instead of falling straight to the code default. Read-only fallback;
   *  "Set as firm default" always writes to the CURRENT `storageKey`. */
  legacyStorageKey?: string;
  /** Selection is STANDARD on every table (checkbox column + Explorer
   *  semantics). Pass false only for a degenerate list where selecting a
   *  row can never mean anything. */
  selectable?: boolean;
  /** `'checkbox'`: a plain row click TOGGLES that row's box (no modifier
   *  keys) and still fires `onRowClick`. Default `'explorer'` is unchanged. */
  selectionMode?: 'explorer' | 'checkbox';
  /** Controlled selection — the ids (stringified `keyField`) that are
   *  selected. Omit and the table owns the selection itself. */
  selectedIds?: Set<string>;
  /** Fires on every selection change with the ids AND the rows they map to,
   *  so a page never has to re-derive items from ids. */
  onSelectionChange?: (ids: Set<string>, items: T[]) => void;
  /** The signed-in user's permission keys (`authUser.permissions`). A bulk
   *  button whose key is missing is HIDDEN, never disabled. */
  permissions?: string[];
  /** The one bulk-ops UX app-wide — caller supplies handlers + keys. */
  bulkActions?: BulkActions<T>;
  /** Enables native HTML5 drag on each row (e.g. dragging a Directory row
   *  onto a team-tree drop target). List/assignment UIs stay click-primary
   *  with drag optional per docs/DESIGN.md §10.2 — this never replaces
   *  onRowClick, it's additive. */
  onRowDragStart?: (item: T, e: React.DragEvent<HTMLTableRowElement>) => void;
  onRowDragEnd?: (item: T, e: React.DragEvent<HTMLTableRowElement>) => void;
  /** Right-click on a row — the caller renders its own kit `ui/context-menu`
   *  at the pointer (DESIGN.md §4 context UI). Additive: omit and a row's
   *  native browser context menu shows, exactly as every table today. */
  onRowContextMenu?: (item: T, e: React.MouseEvent<HTMLTableRowElement>) => void;
  /** Controlled filter state — pass both to let an external surface (e.g. a
   *  Kanban board alongside this table) read/drive the SAME filter the
   *  table's own Filter button edits. Omit either and the table owns its
   *  filter state exactly as before (every existing caller). */
  filterState?: TableFilterState;
  onFilterStateChange?: (state: TableFilterState) => void;
  /** Hides the table's own search input — for a surface whose ONE search
   *  box lives in a shared toolbar above both this table and a sibling view,
   *  and feeds this table already-searched `data`. Omit and the table shows
   *  its usual search box (every existing caller). */
  hideSearchBox?: boolean;
  /** Fires with the rows the table is ACTUALLY showing — the caller's `data`
   *  after the table's own search box, column filters and sort. A page whose
   *  bulk-select lives outside the table (Spec Library "Select all") needs
   *  this: `data` alone still contains rows the search has hidden, so acting
   *  on it would silently select what the user cannot see.
   *  Pass a stable callback (a `useState` setter is stable). */
  onVisibleRowsChange?: (rows: T[]) => void;
  /** A narrow host (e.g. a docked panel) collapses Sort/Group/Filter/View/
   *  the Aa slider behind one "View options" disclosure button instead of
   *  spreading them across the toolbar — every control still works exactly
   *  as before, just reached through one extra click. A dot marks the
   *  button when any of them is off its default. Omit (every existing
   *  caller) and the toolbar renders inline, byte-for-byte unchanged. */
  compactToolbar?: boolean;
  /** A pick dialog has no use for per-user table chrome: hides the saved-view
   *  picker and the Aa text-size slider. Omit (every existing caller) and the
   *  toolbar is unchanged. */
  hideViewChrome?: boolean;
}

// ─── Bulk operations (docs/FORECASTING_V2_TABLE_STANDARD_PLAN.md D2d) ───
//
// Every button is client-gated with hasPermission() against `permissions`
// and HIDDEN when the key is missing. The caller's handler still performs
// the real, server-enforced per-id service call — this bar never writes.

export interface BulkActions<T> {
  /** Soft state change — no confirm, undo offered when `onUndo` is given. */
  archive?: {
    /** One key, or an array = ANY-of (a surface whose server rule accepts
     *  more than one key — e.g. expenses: `expenses.delete` OR
     *  `expenses.edit_own` for your own draft). */
    permission: string | string[];
    onArchive: (items: T[]) => Promise<void> | void;
    onUndo?: (items: T[]) => Promise<void> | void;
    label?: string;
  };
  /** Hard delete — kit confirm first (§4 destructive), danger colour, set
   *  apart from the safe actions. */
  delete?: {
    /** One key, or an array = ANY-of (see `archive.permission`). */
    permission: string | string[];
    onDelete: (items: T[]) => Promise<void> | void;
    onUndo?: (items: T[]) => Promise<void> | void;
    label?: string;
    confirmTitle?: string;
    /** Singular noun for the default confirm title ("Delete 3 masters?"). */
    confirmNoun?: string;
    confirmDescription?: (count: number) => React.ReactNode;
  };
  /** `permission: null` = ungated (an export of what the user can already
   *  see on screen). Omit the whole key to drop the button. */
  exportCsv?: {
    permission: string | string[] | null;
    filename?: string;
  };
  /** Surface-specific bulk buttons, rendered after the count/Clear group and
   *  before Export CSV. The second arg lets a consumer build single-row
   *  buttons (e.g. "Duplicate" only when `count === 1`) and clear the
   *  selection after acting — optional, so existing one-arg callers still
   *  compile. */
  extra?: (items: T[], meta?: { count: number; clear: () => void }) => React.ReactNode;
}

/** Flattens a rendered cell to plain text so CSV exports what the user
 *  SEES. A column with `render` and no `getValue` would otherwise write the
 *  raw stored field (or a blank) into the file. No `react-dom/server` in
 *  this bundle — a recursive walk over the element tree is enough. */
function nodeToText(node: React.ReactNode): string {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(nodeToText).join(' ');
  if (React.isValidElement(node)) return nodeToText((node.props as any)?.children);
  return '';
}

// ─── Filter helpers ───

function buildDistinctOptions<T>(
  col: Column<T>,
  data: T[],
  getColValue: (item: T, col: Column<T>) => string | number
): { value: string; label: string }[] {
  const set = new Set<string>();
  for (const item of data) {
    const v = getColValue(item, col);
    if (v !== '' && v != null) set.add(String(v));
  }
  return Array.from(set)
    .sort((a, b) => a.localeCompare(b))
    .map((v) => ({ value: v, label: v }));
}

export function isFilterActive(fv: ColumnFilterValue | undefined): fv is ColumnFilterValue {
  if (!fv) return false;
  if (fv.type === 'multiselect') return fv.values.length > 0;
  if (fv.type === 'text') return fv.contains.trim().length > 0;
  if (fv.type === 'number-range') return fv.min != null || fv.max != null;
  return !!(fv.min || fv.max);
}

interface Rollup {
  label: string;
  sum: number;
}

function computeNumericRollups<T>(
  items: T[],
  cols: Column<T>[],
  getColValue: (item: T, col: Column<T>) => string | number
): Rollup[] {
  const rollups: Rollup[] = [];
  for (const col of cols) {
    // A column whose values are a fixed code list is never a quantity, even
    // when the codes happen to be numeric — don't sum status ids.
    if (col.filterOptions || col.filterType === 'multiselect') continue;
    let allNumeric = true;
    let sum = 0;
    let any = false;
    for (const item of items) {
      const v = getColValue(item, col);
      if (v === '' || v == null) continue;
      const num = typeof v === 'number' ? v : Number(v);
      if (isNaN(num)) {
        allNumeric = false;
        break;
      }
      sum += num;
      any = true;
    }
    if (allNumeric && any) rollups.push({ label: col.label, sum });
  }
  return rollups;
}

/** Group-header rollup display — trims float noise, keeps it readable. */
function formatRollup(n: number): string {
  const rounded = Math.round(n * 100) / 100;
  return rounded.toLocaleString();
}

/** One filter summary chip's display text, for the active-filters row. */
export function filterSummaryText(col: Column<any>, fv: ColumnFilterValue): string {
  if (fv.type === 'multiselect') {
    return fv.values.length === 1 ? fv.values[0] : `${fv.values.length} selected`;
  }
  if (fv.type === 'text') return `"${fv.contains}"`;
  if (fv.type === 'number-range') {
    if (fv.min != null && fv.max != null) return `${fv.min}–${fv.max}`;
    if (fv.min != null) return `≥ ${fv.min}`;
    return `≤ ${fv.max}`;
  }
  if (fv.min && fv.max) return `${fv.min} – ${fv.max}`;
  if (fv.min) return `from ${fv.min}`;
  return `until ${fv.max}`;
}

// ─── AnchoredPopover — shared portal popover shell for filter/view menus.
// createPortal(document.body), position:fixed, viewport-clamped, dismiss on
// mousedown-outside (excluding the trigger, via ignoreRef), Esc closes. ───
export function AnchoredPopover({
  anchorRect,
  width = 260,
  onClose,
  ignoreRef,
  children,
}: {
  anchorRect: DOMRect;
  width?: number;
  onClose: () => void;
  ignoreRef?: React.RefObject<HTMLElement | null>;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (ref.current?.contains(t)) return;
      if (ignoreRef?.current?.contains(t)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose, ignoreRef]);

  const maxH = 420;
  const left = Math.max(8, Math.min(anchorRect.left, window.innerWidth - width - 8));
  const below = window.innerHeight - anchorRect.bottom - 8;
  const top = below > 200 ? anchorRect.bottom + 4 : Math.max(8, anchorRect.top - maxH - 4);

  return createPortal(
    // Mounting a FocusScope pauses a host Dialog's focus trap so inputs inside
    // this body-portaled popover keep focus (same as SearchableSelect's menu).
    <FocusScope asChild trapped={false}>
      <div
        ref={ref}
        {...appLayerProps}
        style={{
          position: 'fixed',
          left,
          top,
          width,
          maxHeight: maxH,
          background: 'var(--sf)',
          border: '1px solid var(--bd)',
          borderRadius: rem(8),
          boxShadow: '0 8px 24px rgba(0,0,0,.18)',
          zIndex: 2147483000,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {children}
      </div>
    </FocusScope>,
    document.body
  );
}

// ─── Per-column filter editor — renders the right control for the
// auto-detected (or pinned) filter type. ───
export function ColumnFilterEditor<T>({
  meta,
  value,
  onChange,
}: {
  meta: { filterType: ColumnFilterType; options?: { value: string; label: string }[] };
  value: ColumnFilterValue | undefined;
  onChange: (fv: ColumnFilterValue | null) => void;
}) {
  if (meta.filterType === 'multiselect') {
    const options = meta.options || [];
    const selected = value && value.type === 'multiselect' ? value.values : [];
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: rem(2),
          maxHeight: rem(160),
          overflowY: 'auto',
        }}
      >
        {options.length === 0 && (
          <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--tm)', fontStyle: 'italic' }}>
            No values
          </div>
        )}
        {options.map((o) => {
          const checked = selected.includes(o.value);
          return (
            <label
              key={o.value}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: rem(6),
                fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                cursor: 'pointer',
                color: 'var(--tx)',
                padding: `var(--space-1) ${rem(2)}`,
                minHeight: rem(20),
              }}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => {
                  const next = checked
                    ? selected.filter((v) => v !== o.value)
                    : [...selected, o.value];
                  onChange(next.length > 0 ? { type: 'multiselect', values: next } : null);
                }}
              />
              {o.label}
            </label>
          );
        })}
      </div>
    );
  }
  if (meta.filterType === 'text') {
    const text = value && value.type === 'text' ? value.contains : '';
    return (
      <input
        value={text}
        onChange={(e) =>
          onChange(e.target.value ? { type: 'text', contains: e.target.value } : null)
        }
        placeholder="Contains…"
        style={rangeInputStyle}
      />
    );
  }
  if (meta.filterType === 'number-range') {
    const min = value && value.type === 'number-range' ? value.min : undefined;
    const max = value && value.type === 'number-range' ? value.max : undefined;
    return (
      <div style={{ display: 'flex', gap: rem(6) }}>
        <input
          type="number"
          value={min ?? ''}
          onChange={(e) => {
            const v = e.target.value === '' ? undefined : Number(e.target.value);
            const nv: ColumnFilterValue = { type: 'number-range', min: v, max };
            onChange(nv.min == null && nv.max == null ? null : nv);
          }}
          placeholder="Min"
          style={rangeInputStyle}
        />
        <input
          type="number"
          value={max ?? ''}
          onChange={(e) => {
            const v = e.target.value === '' ? undefined : Number(e.target.value);
            const nv: ColumnFilterValue = { type: 'number-range', min, max: v };
            onChange(nv.min == null && nv.max == null ? null : nv);
          }}
          placeholder="Max"
          style={rangeInputStyle}
        />
      </div>
    );
  }
  // date-range
  const min = value && value.type === 'date-range' ? value.min : undefined;
  const max = value && value.type === 'date-range' ? value.max : undefined;
  return (
    <div style={{ display: 'flex', gap: rem(6) }}>
      <input
        type="date"
        value={min || ''}
        onChange={(e) => {
          const nv: ColumnFilterValue = {
            type: 'date-range',
            min: e.target.value || undefined,
            max,
          };
          onChange(!nv.min && !nv.max ? null : nv);
        }}
        style={rangeInputStyle}
      />
      <input
        type="date"
        value={max || ''}
        onChange={(e) => {
          const nv: ColumnFilterValue = {
            type: 'date-range',
            min,
            max: e.target.value || undefined,
          };
          onChange(!nv.min && !nv.max ? null : nv);
        }}
        style={rangeInputStyle}
      />
    </div>
  );
}

// ─── Filter bar popover — lists every filterable column with its editor.
// Built once inside DataTable's toolbar so every consumer inherits it. ───
export function FilterMenu<T>({
  columns,
  columnMeta,
  filters,
  onChange,
  onClose,
  anchorRect,
  ignoreRef,
}: {
  columns: Column<T>[];
  columnMeta: Record<
    string,
    { filterType: ColumnFilterType; options?: { value: string; label: string }[] }
  >;
  filters: ColumnFilters;
  onChange: (next: ColumnFilters) => void;
  onClose: () => void;
  anchorRect: DOMRect;
  ignoreRef?: React.RefObject<HTMLElement | null>;
}) {
  const [search, setSearch] = useState('');
  const visibleCols = search
    ? columns.filter((c) => c.label.toLowerCase().includes(search.toLowerCase()))
    : columns;
  const hasActive = Object.keys(filters).some((k) => isFilterActive(filters[k]));

  function setColumnFilter(key: string, fv: ColumnFilterValue | null) {
    const next = { ...filters };
    if (fv === null) delete next[key];
    else next[key] = fv;
    onChange(next);
  }

  return (
    <AnchoredPopover anchorRect={anchorRect} width={280} onClose={onClose} ignoreRef={ignoreRef}>
      <div
        style={{
          padding: `var(--space-2) ${rem(10)}`,
          borderBottom: '1px solid var(--bd)',
          display: 'flex',
          alignItems: 'center',
          gap: rem(6),
          flexShrink: 0,
        }}
      >
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search columns…"
          autoFocus
          style={{ ...rangeInputStyle, flex: 1 }}
        />
        {hasActive && (
          <button
            onClick={() => onChange({})}
            style={{ ...pillBtn, fontSize: 'var(--font-size-xs)', whiteSpace: 'nowrap' }}
          >
            Clear all
          </button>
        )}
      </div>
      <div style={{ overflowY: 'auto', flex: 1 }}>
        {visibleCols.length === 0 && (
          <div
            style={{
              padding: 'var(--space-4) var(--space-3)',
              fontSize: 'calc(var(--font-size-base) * 12 / 13)',
              color: 'var(--tm)',
              textAlign: 'center',
            }}
          >
            No matching columns
          </div>
        )}
        {visibleCols.map((col) => {
          const meta = columnMeta[col.key];
          if (!meta) return null;
          return (
            <div
              key={col.key}
              style={{
                padding: 'var(--space-2) var(--space-3)',
                borderBottom: '1px solid var(--bd)',
              }}
            >
              <div
                style={{
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 700,
                  color: 'var(--tm)',
                  marginBottom: rem(6),
                  textTransform: 'uppercase',
                  letterSpacing: '.03em',
                }}
              >
                {col.label}
              </div>
              <ColumnFilterEditor
                meta={meta}
                value={filters[col.key]}
                onChange={(fv) => setColumnFilter(col.key, fv)}
              />
            </div>
          );
        })}
      </div>
    </AnchoredPopover>
  );
}

// ─── Column prefs ladder — raw-value I/O helpers ───
// The resolver (columnPrefs.ts) is pure; these do the small bit of reading
// that feeds it. Cloud (Prefs) wins once loaded; local cache otherwise. An
// explicit `null` (written by "Reset to default") means "no pref" even
// once the cloud has synced — it is the cross-device reset signal.
function readUserColumnList(key: string): string[] | null {
  if (Prefs.isReady()) {
    const v = Prefs.get(key);
    if (v === null) return null;
    if (Array.isArray(v)) return v;
  }
  try {
    const s = localStorage.getItem(key);
    if (s) {
      const parsed = JSON.parse(s);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch {}
  return null;
}

function readUserColumnTimestamp(key: string): string | null {
  if (Prefs.isReady()) {
    const v = Prefs.get(key);
    if (v === null) return null;
    if (typeof v === 'string') return v;
  }
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

// ─── Component ───

export function DataTable<T>({
  columns: initialColumns,
  data,
  keyField,
  onRowClick,
  rowHref,
  groupOptions,
  defaultGroupKey,
  searchPlaceholder = 'Search...',
  initialSort,
  emptyIcon = '\uD83D\uDCC4',
  emptyTitle = 'No Data',
  emptyMessage = 'Nothing to show yet.',
  toolbarExtra,
  storageKey,
  legacyStorageKey,
  selectable = true,
  selectionMode = 'explorer',
  selectedIds,
  onSelectionChange,
  permissions,
  bulkActions,
  onRowDragStart,
  onRowDragEnd,
  onRowContextMenu,
  filterState: filterStateProp,
  onFilterStateChange: onFilterStateChangeProp,
  hideSearchBox,
  onVisibleRowsChange,
  compactToolbar = false,
  hideViewChrome = false,
}: DataTableProps<T>) {
  // ─── State ───
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState(() => {
    if (storageKey) {
      try {
        const s = localStorage.getItem(`dt_sortKey_${storageKey}`);
        if (s) return s;
      } catch {}
    }
    return initialSort?.key || initialColumns[0]?.key || '';
  });
  const [sortAsc, setSortAsc] = useState(() => {
    if (storageKey) {
      try {
        const s = localStorage.getItem(`dt_sortAsc_${storageKey}`);
        if (s) return s === 'true';
      } catch {}
    }
    return initialSort?.direction !== 'desc';
  });
  const [groupKey, setGroupKey] = useState(() => {
    if (storageKey) {
      try {
        const s = localStorage.getItem(`dt_groupKey_${storageKey}`);
        if (s) return s;
      } catch {}
    }
    return defaultGroupKey ?? 'none';
  });
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => {
    if (storageKey) {
      try {
        const s = localStorage.getItem(`dt_collapsed_${storageKey}`);
        if (s) return new Set(JSON.parse(s));
      } catch {}
    }
    return new Set();
  });
  // Filter state (v2 rules, groups-ready). Every read path — localStorage,
  // the per-user Prefs mirror, saved-view configs — goes through
  // migrateFilterState, so filters saved in the old per-column format keep
  // working; writes always emit v2.
  const [internalFilterState, setInternalFilterState] = useState<TableFilterState>(() => {
    if (storageKey) {
      try {
        const s = localStorage.getItem(`dt_filters_${storageKey}`);
        if (s) return migrateFilterState(JSON.parse(s));
      } catch {}
    }
    return EMPTY_FILTER_STATE;
  });
  // Controlled/uncontrolled hybrid (`filterStateProp`/`onFilterStateChangeProp`)
  // — omit both (every caller before Tasks Kit) and this is byte-identical to
  // the plain `useState` above, persistence effects included.
  const filterState = filterStateProp !== undefined ? filterStateProp : internalFilterState;
  const setFilterState = onFilterStateChangeProp || setInternalFilterState;
  // Text size — a percent of the app's base font (100 = today's default,
  // 13px), never raw px (DESIGN.md §3.6). Migrates any legacy per-table px
  // value (`dt_fontSize_<key>`) to the new percent key once, then drops it.
  const [textScale, setTextScale] = useState(() => {
    if (storageKey) {
      try {
        const pct = localStorage.getItem(`dt_textScale_${storageKey}`);
        if (pct) {
          const p = Number(pct);
          // Garbage in storage must not become a NaN font-size (which would
          // invalidate the whole cascade below and make the slider uncontrolled).
          if (Number.isFinite(p)) return Math.min(140, Math.max(75, p));
        }
        const px = localStorage.getItem(`dt_fontSize_${storageKey}`);
        if (px) {
          const n = Number(px);
          if (Number.isFinite(n)) {
            const migrated = Math.min(140, Math.max(75, Math.round(((n / 13) * 100) / 5) * 5));
            try {
              localStorage.setItem(`dt_textScale_${storageKey}`, String(migrated));
              localStorage.removeItem(`dt_fontSize_${storageKey}`);
            } catch {}
            return migrated;
          }
        }
      } catch {}
    }
    return 100;
  });
  // Column widths are stored as unscaled px; rendered width follows the
  // app-wide UI dial/user scale so columns aren't a fixed size on screen
  // (DESIGN.md §3.6). Subscribed so a dial change (resize/Ctrl+=) re-renders.
  const [uiEffectiveScale, setUiEffectiveScale] = useState(() => getUiScale().effective);
  useEffect(() => subscribeUiScale((v) => setUiEffectiveScale(v.effective)), []);
  // Combined factor for anything that sizes a widget in authored px (column
  // widths, actions column) — the app-wide dial times this table's own text
  // slider, so every column/icon/widget on the surface scales together
  // (Josh 2026-09-09: all tables, all columns/text/icons/widgets follow the
  // slider).
  const renderScale = uiEffectiveScale * (textScale / 100);

  // Column order (array of keys) — persisted if storageKey provided
  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    if (storageKey) {
      try {
        const s = localStorage.getItem(`dt_order_${storageKey}`);
        if (s) return JSON.parse(s);
      } catch {}
    }
    return initialColumns.map((c) => c.key);
  });
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => {
    if (storageKey) {
      try {
        const s = localStorage.getItem(`dt_widths_${storageKey}`);
        if (s) return clampSavedWidths(JSON.parse(s), initialColumns);
      } catch {}
    }
    const w: Record<string, number> = {};
    for (const c of initialColumns) w[c.key] = c.width || 150;
    return w;
  });

  useEffect(() => {
    setColumnOrder((prev) => {
      const newKeys = initialColumns.map((c) => c.key);
      const existing = prev.filter((k) => newKeys.includes(k));
      const added = newKeys.filter((k) => !prev.includes(k));
      return [...existing, ...added];
    });
  }, [initialColumns.map((c) => c.key).join(',')]);

  // ── Per-user server sync: the user's saved layout (user_prefs) wins over
  // localStorage once loaded, so tables look the same on every device and on
  // web/desktop alike. Applied ONCE per mount, before any local resize.
  const prefsAppliedRef = useRef(false);
  const [prefsTick, setPrefsTick] = useState(0);
  useEffect(() => Prefs.subscribe(() => setPrefsTick((v) => v + 1)), []);
  useEffect(() => {
    if (prefsAppliedRef.current || !storageKey || !Prefs.isReady()) return;
    prefsAppliedRef.current = true;
    const w = Prefs.get(`dt_widths_${storageKey}`);
    if (w && typeof w === 'object')
      setColumnWidths((cur) => clampSavedWidths({ ...cur, ...w }, initialColumns));
    const ts = Prefs.get(`dt_textScale_${storageKey}`);
    if (typeof ts === 'number' && Number.isFinite(ts))
      setTextScale(Math.min(140, Math.max(75, ts)));
    // Column order + hidden columns are resolved through the firm-default
    // ladder below (colPrefsApplied effect), not applied raw here.
    const sk = Prefs.get(`dt_sortKey_${storageKey}`);
    if (typeof sk === 'string' && sk) setSortKey(sk);
    const sa = Prefs.get(`dt_sortAsc_${storageKey}`);
    if (typeof sa === 'boolean') setSortAsc(sa);
    const gk = Prefs.get(`dt_groupKey_${storageKey}`);
    if (typeof gk === 'string' && gk) setGroupKey(gk);
    const cg = Prefs.get(`dt_collapsed_${storageKey}`);
    if (Array.isArray(cg)) setCollapsedGroups(new Set(cg));
    const cf = Prefs.get(`dt_filters_${storageKey}`);
    if (cf && typeof cf === 'object') setFilterState(migrateFilterState(cf));
  }, [prefsTick, storageKey]);

  // Persist column widths to localStorage (debounced to avoid thrashing during
  // resize) + the per-user server copy.
  const widthsTimer = useRef<any>(null);
  useEffect(() => {
    if (!storageKey) return;
    if (widthsTimer.current) clearTimeout(widthsTimer.current);
    widthsTimer.current = setTimeout(() => {
      try {
        localStorage.setItem(`dt_widths_${storageKey}`, JSON.stringify(columnWidths));
      } catch {}
      // Harness guard (feedback_harness_must_not_write_user_cloud_state): a QA run signed in as a real
      // account must never rewrite that account's cloud column widths.
      if (prefsAppliedRef.current && localStorage.getItem('sf.dockNoCloudPersist') !== '1')
        Prefs.set(`dt_widths_${storageKey}`, columnWidths);
    }, 300);
  }, [columnWidths, storageKey]);
  useEffect(() => {
    if (storageKey) {
      try {
        localStorage.setItem(`dt_order_${storageKey}`, JSON.stringify(columnOrder));
      } catch {}
      if (prefsAppliedRef.current) Prefs.set(`dt_order_${storageKey}`, columnOrder);
    }
  }, [columnOrder, storageKey]);
  useEffect(() => {
    if (storageKey) {
      try {
        localStorage.setItem(`dt_textScale_${storageKey}`, String(textScale));
      } catch {}
      // Harness guard (feedback_harness_must_not_write_user_cloud_state): a QA run signed in as a real
      // account must never rewrite that account's cloud text scale.
      if (prefsAppliedRef.current && localStorage.getItem('sf.dockNoCloudPersist') !== '1')
        Prefs.set(`dt_textScale_${storageKey}`, textScale);
    }
  }, [textScale, storageKey]);
  useEffect(() => {
    if (storageKey) {
      try {
        localStorage.setItem(`dt_collapsed_${storageKey}`, JSON.stringify([...collapsedGroups]));
      } catch {}
      if (prefsAppliedRef.current) Prefs.set(`dt_collapsed_${storageKey}`, [...collapsedGroups]);
    }
  }, [collapsedGroups, storageKey]);
  useEffect(() => {
    if (storageKey) {
      try {
        localStorage.setItem(`dt_sortKey_${storageKey}`, sortKey);
      } catch {}
      if (prefsAppliedRef.current) Prefs.set(`dt_sortKey_${storageKey}`, sortKey);
    }
  }, [sortKey, storageKey]);
  useEffect(() => {
    if (storageKey) {
      try {
        localStorage.setItem(`dt_sortAsc_${storageKey}`, String(sortAsc));
      } catch {}
      if (prefsAppliedRef.current) Prefs.set(`dt_sortAsc_${storageKey}`, sortAsc);
    }
  }, [sortAsc, storageKey]);
  useEffect(() => {
    if (storageKey) {
      try {
        localStorage.setItem(`dt_groupKey_${storageKey}`, groupKey);
      } catch {}
      if (prefsAppliedRef.current) Prefs.set(`dt_groupKey_${storageKey}`, groupKey);
    }
  }, [groupKey, storageKey]);
  useEffect(() => {
    if (storageKey) {
      try {
        localStorage.setItem(`dt_filters_${storageKey}`, JSON.stringify(filterState));
      } catch {}
      if (prefsAppliedRef.current) Prefs.set(`dt_filters_${storageKey}`, filterState);
    }
  }, [filterState, storageKey]);

  // Hidden columns — persisted set of column keys the user chose to hide
  const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(() => {
    if (storageKey) {
      try {
        const s = localStorage.getItem(`dt_hidden_${storageKey}`);
        if (s) return new Set(JSON.parse(s));
      } catch {}
    }
    return new Set(initialColumns.filter((c) => c.defaultHidden).map((c) => c.key));
  });
  useEffect(() => {
    if (storageKey) {
      try {
        localStorage.setItem(`dt_hidden_${storageKey}`, JSON.stringify([...hiddenColumns]));
      } catch {}
      if (prefsAppliedRef.current) Prefs.set(`dt_hidden_${storageKey}`, [...hiddenColumns]);
    }
  }, [hiddenColumns, storageKey]);

  // ── Firm-default column ladder (table-firm-defaults plan) ──
  // The user's own saved columns/order -> the FIRM default (admin_center.
  // manage_firm_settings, firm_prefs) -> the code's defaultHidden flags —
  // resolveColumnPrefs (columnPrefs.ts) is the pure ladder; this effect
  // feeds it and re-runs it whenever new information arrives (FirmPrefs
  // load/refetch/set, or a cross-device Prefs refetch), so a firm default
  // someone else just published reaches this table live. `colPrefsApplied`
  // starts true when both sides are ALREADY loaded (the common case after
  // the first table of the session) so there is nothing to gate; it starts
  // false only on the very first table shown right after sign-in, and the
  // render below shows a skeleton until this effect has resolved once —
  // never the code default painted, then flipped (feedback_lean_verify /
  // the plan's "no flicker" requirement).
  const [firmPrefsTick, setFirmPrefsTick] = useState(0);
  useEffect(() => FirmPrefs.subscribe(() => setFirmPrefsTick((v) => v + 1)), []);
  const [colPrefsApplied, setColPrefsApplied] = useState<boolean>(
    () => !storageKey || (FirmPrefs.isReady() && Prefs.isReady())
  );
  const firmColumnDefault: FirmColumnDefault | null = useMemo(() => {
    if (!storageKey) return null;
    let raw = FirmPrefs.get(`dt_columns.${storageKey}`) as Partial<FirmColumnDefault> | undefined;
    // No firm default at the current key — a prior key (storage-key rename)
    // may still have one. Read-only fallback; never writes the legacy key.
    if ((!raw || typeof raw !== 'object') && legacyStorageKey) {
      raw = FirmPrefs.get(`dt_columns.${legacyStorageKey}`) as
        | Partial<FirmColumnDefault>
        | undefined;
    }
    if (!raw || typeof raw !== 'object') return null;
    if (!Array.isArray(raw.hidden) || !Array.isArray(raw.order) || typeof raw.setAt !== 'string')
      return null;
    return { hidden: raw.hidden, order: raw.order, setAt: raw.setAt, setBy: raw.setBy };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey, legacyStorageKey, firmPrefsTick]);
  useEffect(() => {
    if (!storageKey) return;
    if (!(FirmPrefs.isReady() && Prefs.isReady())) return;
    const codeOrder = initialColumns.map((c) => c.key);
    const codeDefaultHidden = initialColumns.filter((c) => c.defaultHidden).map((c) => c.key);
    const resolved = resolveColumnPrefs({
      userHidden: readUserColumnList(`dt_hidden_${storageKey}`),
      userHiddenAt: readUserColumnTimestamp(`dt_hiddenAt_${storageKey}`),
      userOrder: readUserColumnList(`dt_order_${storageKey}`),
      userOrderAt: readUserColumnTimestamp(`dt_orderAt_${storageKey}`),
      firmDefault: firmColumnDefault,
      codeDefaultHidden,
      codeOrder,
    });
    setHiddenColumns(new Set(resolved.hidden));
    setColumnOrder(resolved.order);
    setColPrefsApplied(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    storageKey,
    firmPrefsTick,
    prefsTick,
    firmColumnDefault,
    initialColumns.map((c) => c.key).join(','),
    // A host whose `defaultHidden` flags depend on something it measures
    // AFTER mount (the Modules panel's narrow-dock column trim) renders
    // with the code default FALSE on its first pass, then flips it true a
    // tick later once the ResizeObserver reports back — same column KEYS
    // both times, so the dependency above alone never re-ran this ladder
    // and the narrow trim never actually applied. Every other caller's
    // `defaultHidden` flags are static, so this string never changes for
    // them — zero extra reruns, same behavior as before.
    initialColumns
      .filter((c) => c.defaultHidden)
      .map((c) => c.key)
      .join(','),
  ]);

  // "Set as firm default" is an admin act (admin_center.manage_firm_settings
  // — same key that gates the firm_prefs row server-side); hidden, not
  // disabled, for anyone who lacks it, per the app's convention. "Reset to
  // default" has no permission gate — it only touches the caller's own pref.
  const canSetFirmColumnDefault = hasPermission(
    permissions || [],
    'admin_center.manage_firm_settings'
  );
  const [colActionBusy, setColActionBusy] = useState(false);
  const [colActionStatus, setColActionStatus] = useState<{
    text: string;
    error?: boolean;
  } | null>(null);
  const colActionTimer = useRef<any>(null);
  useEffect(() => () => clearTimeout(colActionTimer.current), []);
  function showColActionStatus(text: string, error?: boolean) {
    clearTimeout(colActionTimer.current);
    setColActionStatus({ text, error });
    colActionTimer.current = setTimeout(() => setColActionStatus(null), 5000);
  }

  function handleResetColumnsToDefault() {
    if (!storageKey) return;
    for (const suffix of ['hidden', 'hiddenAt', 'order', 'orderAt']) {
      const k = `dt_${suffix}_${storageKey}`;
      try {
        localStorage.removeItem(k);
      } catch {}
      if (prefsAppliedRef.current) Prefs.set(k, null);
    }
    const codeOrder = initialColumns.map((c) => c.key);
    const codeDefaultHidden = initialColumns.filter((c) => c.defaultHidden).map((c) => c.key);
    const resolved = resolveColumnPrefs({
      userHidden: null,
      userHiddenAt: null,
      userOrder: null,
      userOrderAt: null,
      firmDefault: firmColumnDefault,
      codeDefaultHidden,
      codeOrder,
    });
    setHiddenColumns(new Set(resolved.hidden));
    setColumnOrder(resolved.order);
    showColActionStatus('Columns reset to default');
  }

  async function handleSetFirmColumnDefault() {
    if (!storageKey) return;
    setColActionBusy(true);
    try {
      const value: FirmColumnDefault = {
        hidden: [...hiddenColumns],
        order: columnOrder,
        setAt: new Date().toISOString(),
        setBy: Prefs.currentUserId() || undefined,
      };
      await FirmPrefs.set(`dt_columns.${storageKey}`, value);
      showColActionStatus('Firm default saved — everyone sees these columns by default');
    } catch (e: any) {
      showColActionStatus(e?.message || 'Could not save the firm default.', true);
    } finally {
      setColActionBusy(false);
    }
  }

  // ─── Compact toolbar (narrow docked panel) — "View options" disclosure.
  // Captured once on mount so toggling a control never moves what counts
  // as "default" for the dot indicator.
  const defaultToolbarRef = useRef({ sortKey, sortAsc });
  const [viewOptionsOpen, setViewOptionsOpen] = useState(false);
  const viewOptionsBtnRef = useRef<HTMLButtonElement>(null);

  // Column visibility context menu
  const [colMenuPos, setColMenuPos] = useState<{ x: number; y: number } | null>(null);

  const allOrderedColumns = useMemo(() => {
    const colMap = new Map(initialColumns.map((c) => [c.key, c]));
    return columnOrder.map((k) => colMap.get(k)).filter(Boolean) as Column<T>[];
  }, [columnOrder, initialColumns]);

  const orderedColumns = useMemo(() => {
    return allOrderedColumns.filter((c) => !hiddenColumns.has(c.key));
  }, [allOrderedColumns, hiddenColumns]);

  // ─── Search / Sort / Group ───

  const getColValue = useCallback((item: T, col: Column<T>): string | number => {
    if (col.getValue) return col.getValue(item);
    const v = (item as any)[col.key];
    return v == null ? '' : String(v);
  }, []);

  // ─── Column filter metadata (ultra filtering) ───
  // Auto-detects the right control per column from the data itself, unless
  // the caller pins one via Column.filterType. Distinct-value lists are
  // built from the full dataset (not the already-filtered list) so option
  // sets stay stable as other filters are applied — a standard faceted-
  // filter tradeoff, not a live facet count.
  const columnMeta = useMemo(() => {
    const meta: Record<
      string,
      { filterType: ColumnFilterType; options?: { value: string; label: string }[] }
    > = {};
    for (const col of initialColumns) {
      if (col.filterType === 'none') continue;
      if (col.filterType) {
        meta[col.key] =
          col.filterType === 'multiselect'
            ? {
                filterType: 'multiselect',
                options: col.filterOptions || buildDistinctOptions(col, data, getColValue),
              }
            : { filterType: col.filterType };
        continue;
      }
      // An explicit value/label list means a fixed code list — always a
      // multiselect, labelled the way the user knows it.
      if (col.filterOptions) {
        meta[col.key] = { filterType: 'multiselect', options: col.filterOptions };
        continue;
      }
      const values = data
        .map((item) => getColValue(item, col))
        .filter((v) => v !== '' && v != null);
      if (values.length === 0) {
        meta[col.key] = { filterType: 'text' };
        continue;
      }
      const allNumeric = values.every(
        (v) => typeof v === 'number' || (String(v).trim() !== '' && !isNaN(Number(v)))
      );
      const distinct = new Set(values.map(String));
      if (allNumeric && distinct.size > 8) {
        meta[col.key] = { filterType: 'number-range' };
        continue;
      }
      const looksDate =
        /date|At$/i.test(col.key) &&
        !allNumeric &&
        values.every((v) => !isNaN(Date.parse(String(v))));
      if (looksDate) {
        meta[col.key] = { filterType: 'date-range' };
        continue;
      }
      if (distinct.size > 0 && distinct.size <= 25 && distinct.size < values.length) {
        meta[col.key] = {
          filterType: 'multiselect',
          options: buildDistinctOptions(col, data, getColValue),
        };
        continue;
      }
      meta[col.key] = { filterType: 'text' };
    }
    return meta;
  }, [data, initialColumns, getColValue]);

  const filterableColumns = useMemo(
    () => initialColumns.filter((c) => columnMeta[c.key]),
    [initialColumns, columnMeta]
  );

  /** Left-rail field list for the Filter Builder — label, control kind and
   *  (for list fields) the distinct values already computed above. */
  const filterFields = useMemo<FilterField[]>(
    () =>
      filterableColumns.map((c) => ({
        key: c.key,
        label: c.label,
        kind: fieldKind(columnMeta[c.key].filterType),
        options: columnMeta[c.key].options,
      })),
    [filterableColumns, columnMeta]
  );

  const activeRules = useMemo(
    () =>
      filterState.rules.filter((r) =>
        isFilterGroup(r) ? r.rules.some(isRuleActive) : isRuleActive(r)
      ),
    [filterState]
  );
  const activeCount = activeRuleCount(filterState);

  /** Row → the display value a rule tests, matching what the filter UI
   *  shows. `undefined` means the column no longer exists, which makes the
   *  rule inert instead of blanking the table. */
  const resolveFieldValue = useCallback(
    (item: T, field: string): string | number | string[] | undefined => {
      const col = initialColumns.find((c) => c.key === field);
      if (!col) return undefined;
      if (col.filterValue) return col.filterValue(item);
      return getColValue(item, col);
    },
    [initialColumns, getColValue]
  );

  const matchesFilters = useCallback(
    (item: T, state: TableFilterState) =>
      matchesFilterState(state, (f) => resolveFieldValue(item, f)),
    [resolveFieldValue]
  );

  const fieldLabel = useCallback(
    (key: string) => initialColumns.find((c) => c.key === key)?.label || key,
    [initialColumns]
  );

  /** Chips show the label the user picked ("Active"), not the stored value. */
  const fieldValueLabel = useCallback(
    (key: string, v: string) => columnMeta[key]?.options?.find((o) => o.value === v)?.label || v,
    [columnMeta]
  );

  /** Live "N of M rows match" for the builder's draft. */
  const countMatches = useCallback(
    (state: TableFilterState) =>
      data.reduce((n, item) => (matchesFilters(item, state) ? n + 1 : n), 0),
    [data, matchesFilters]
  );

  const filtered = useMemo(() => {
    let list = data;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((item) =>
        initialColumns.some((col) => {
          const v = getColValue(item, col);
          return String(v).toLowerCase().includes(q);
        })
      );
    }
    if (activeRules.length > 0) {
      list = list.filter((item) => matchesFilters(item, filterState));
    }
    const sortCol = initialColumns.find((c) => c.key === sortKey);
    if (sortCol && sortCol.sortable !== false) {
      list = [...list].sort((a, b) => {
        const va = getColValue(a, sortCol);
        const vb = getColValue(b, sortCol);
        if (typeof va === 'number' && typeof vb === 'number') return va - vb;
        const sa = String(va).toLowerCase(),
          sb = String(vb).toLowerCase();
        if (sa < sb) return -1;
        if (sa > sb) return 1;
        return 0;
      });
      if (!sortAsc) list = list.reverse();
    }
    return list;
  }, [
    data,
    search,
    sortKey,
    sortAsc,
    initialColumns,
    getColValue,
    activeRules,
    filterState,
    matchesFilters,
  ]);

  // Group by ANY column — `groupOptions` (if passed) overrides the label/
  // derivation for specific columns; every other column still groups using
  // its own display value. Auto-added "Ungrouped" for missing values. A
  // passed option whose key matches no column (there is no "Division"
  // column, only a derived one) is appended rather than dropped — see
  // `mergeGroupOptions`.
  const effectiveGroupOptions = useMemo<GroupOption<T>[]>(
    () =>
      mergeGroupOptions(initialColumns, groupOptions || [], (col) => ({
        key: col.key,
        label: col.label,
        getValue: (item: T) => String(getColValue(item, col) || 'Ungrouped'),
      })),
    [groupOptions, initialColumns, getColValue]
  );

  const grouped = useMemo(() => {
    if (groupKey === 'none') return [{ label: '', items: filtered, rollups: [] as Rollup[] }];
    const opt = effectiveGroupOptions.find((g) => g.key === groupKey);
    if (!opt) return [{ label: '', items: filtered, rollups: [] as Rollup[] }];
    const map = new Map<string, T[]>();
    for (const item of filtered) {
      const g = opt.getValue(item) || 'Ungrouped';
      if (!map.has(g)) map.set(g, []);
      map.get(g)!.push(item);
    }
    return Array.from(map.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([label, items]) => ({
        label,
        items,
        rollups: computeNumericRollups(items, orderedColumns, getColValue),
      }));
  }, [filtered, groupKey, effectiveGroupOptions, orderedColumns, getColValue]);

  /** A group is collapsed when grouping is on, it has a label, and the user
   *  folded it. The render path and the selection path MUST agree — one
   *  predicate, used by both. */
  const isGroupCollapsed = useCallback(
    (label: string) => groupKey !== 'none' && !!label && collapsedGroups.has(label),
    [groupKey, collapsedGroups]
  );

  // What the user can actually SEE — a collapsed group's rows are not on
  // screen, so select-all, a Shift range and the caller's visible-rows
  // callback must all skip them.
  const visibleItems = useMemo(
    () => grouped.filter((g) => !isGroupCollapsed(g.label)).flatMap((g) => g.items),
    [grouped, isGroupCollapsed]
  );

  // Tell the caller what is on screen.
  useEffect(() => {
    onVisibleRowsChange?.(visibleItems);
  }, [visibleItems, onVisibleRowsChange]);

  // ─── Filter Builder modal (toolbar "Filter" button) ───
  const [filterBuilderOpen, setFilterBuilderOpen] = useState(false);
  const filterBtnRef = useRef<HTMLButtonElement>(null);

  // ─── Named saved views (DashboardViewService) ───
  // Surface key derives from storageKey — one logical surface per table,
  // isolated from any page-level dashboard-widget saved views that may
  // also use DashboardViewService under a different surface name.
  const viewSurface = storageKey ? `datatable.${storageKey}` : null;
  const [savedViews, setSavedViews] = useState<DashboardView[]>([]);
  const [activeViewId, setActiveViewId] = useState<string | null>(null);
  const [viewSaveOpen, setViewSaveOpen] = useState(false);
  const [viewSaveName, setViewSaveName] = useState('');
  const [viewSaveShared, setViewSaveShared] = useState(false);
  const [confirmDeleteView, setConfirmDeleteView] = useState<DashboardView | null>(null);
  const viewSaveBtnRef = useRef<HTMLButtonElement>(null);
  const viewSelectBtnRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!viewSurface) return;
    const uid = Prefs.currentUserId();
    if (!uid) return;
    let cancelled = false;
    DashboardViewService.listForSurface(viewSurface, uid)
      .then((vs) => {
        if (!cancelled) setSavedViews(vs);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // Re-check once Prefs finishes loading (uid becomes available shortly after auth).
  }, [viewSurface, prefsTick]);

  function applySavedView(v: DashboardView) {
    const c = (v.config || {}) as Record<string, any>;
    if (typeof c.sortKey === 'string') setSortKey(c.sortKey);
    if (typeof c.sortAsc === 'boolean') setSortAsc(c.sortAsc);
    if (typeof c.groupKey === 'string') setGroupKey(c.groupKey);
    if (Array.isArray(c.collapsedGroups)) setCollapsedGroups(new Set(c.collapsedGroups));
    // Views saved before the Filter Builder carry `columnFilters` in the old
    // per-column shape; new ones carry `filterState` (v2). Both migrate.
    const savedFilters = c.filterState ?? c.columnFilters;
    if (savedFilters && typeof savedFilters === 'object') {
      setFilterState(migrateFilterState(savedFilters));
    }
    if (Array.isArray(c.columnOrder) && c.columnOrder.length > 0) {
      setColumnOrder(() => {
        const newKeys = initialColumns.map((cc) => cc.key);
        const existing = c.columnOrder.filter((k: string) => newKeys.includes(k));
        const added = newKeys.filter((k) => !existing.includes(k));
        return [...existing, ...added];
      });
      // Applying a view IS the user's own choice of order — stamp it so a
      // firm default published before this moment doesn't override it on
      // the next reload (see the drag-reorder handler's comment).
      if (storageKey) {
        const now = new Date().toISOString();
        try {
          localStorage.setItem(`dt_orderAt_${storageKey}`, now);
        } catch {}
        if (prefsAppliedRef.current) Prefs.set(`dt_orderAt_${storageKey}`, now);
      }
    }
    if (c.columnWidths && typeof c.columnWidths === 'object') {
      setColumnWidths((cur) => ({ ...cur, ...c.columnWidths }));
    }
    setActiveViewId(v.id);
  }

  async function saveCurrentView(name: string, shared: boolean, existingId?: string) {
    const uid = Prefs.currentUserId();
    if (!uid || !viewSurface || !name.trim()) return;
    const existing = existingId ? savedViews.find((v) => v.id === existingId) : undefined;
    const config = {
      sortKey,
      sortAsc,
      groupKey,
      collapsedGroups: [...collapsedGroups],
      filterState,
      columnOrder,
      columnWidths,
    };
    const view: DashboardView = {
      id: existing?.id || '',
      userId: uid,
      surface: viewSurface,
      name: name.trim(),
      config,
      shared,
      isDefault: existing?.isDefault || false,
    };
    const saved = await DashboardViewService.save(view);
    setSavedViews((prev) => [...prev.filter((v) => v.id !== saved.id), saved]);
    setActiveViewId(saved.id);
  }

  async function deleteSavedView(id: string) {
    await DashboardViewService.delete(id);
    setSavedViews((prev) => prev.filter((v) => v.id !== id));
    if (activeViewId === id) setActiveViewId(null);
  }

  async function setViewDefault(id: string) {
    const uid = Prefs.currentUserId();
    if (!uid || !viewSurface) return;
    await DashboardViewService.setDefault(id, viewSurface, uid);
    const vs = await DashboardViewService.listForSurface(viewSurface, uid);
    setSavedViews(vs);
  }

  const activeView = savedViews.find((v) => v.id === activeViewId) || null;

  // ─── Row selection (Explorer semantics — lib/listSelection is the ONE
  // implementation of the rules; this only wires them to rows) ───

  const idOf = useCallback((item: T) => String((item as any)[keyField]), [keyField]);
  /** Stable render order a Shift range runs in — post search/filter/sort,
   *  across every EXPANDED group, exactly what the user sees. */
  const visibleIds = useMemo(() => visibleItems.map(idOf), [visibleItems, idOf]);
  const itemById = useMemo(() => {
    const m = new Map<string, T>();
    for (const item of data) m.set(idOf(item), item);
    return m;
  }, [data, idOf]);

  const [innerSelected, setInnerSelected] = useState<Set<string>>(() => new Set());
  const selected = selectedIds ?? innerSelected;
  const selectionAnchor = useRef<string | null>(null);

  const applySelection = useCallback(
    (ids: string[]) => {
      const next = new Set(ids);
      if (!selectedIds) setInnerSelected(next);
      onSelectionChange?.(
        next,
        ids.map((id) => itemById.get(id)).filter((x): x is T => x !== undefined)
      );
    },
    [selectedIds, onSelectionChange, itemById]
  );

  // A row the data no longer holds cannot stay selected — otherwise a bulk
  // action later fires against a ghost id.
  useEffect(() => {
    if (selected.size === 0 || selectionMode === 'checkbox') return;
    const kept = [...selected].filter((id) => itemById.has(id));
    if (kept.length !== selected.size) applySelection(kept);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemById]);

  /** EVERY selected row, drawn from the full dataset — not from the filtered
   *  list. Select ten rows then type in the search box and the bar still says
   *  "10 selected"; the confirm, the handlers and the CSV must act on those
   *  same ten, never on the visible subset. */
  const selectedItems = useMemo(
    () => data.filter((item) => selected.has(idOf(item))),
    [data, selected, idOf]
  );
  const selectedCount = selected.size;
  const allVisibleSelected = allRowsSelected(visibleIds, selected);
  const someVisibleSelected = !allVisibleSelected && visibleIds.some((id) => selected.has(id));

  function selectByGesture(id: string, mods: { ctrl: boolean; shift: boolean }) {
    const res = nextSelectionByKey([...selected], selectionAnchor.current, id, visibleIds, mods);
    selectionAnchor.current = res.anchor;
    applySelection(res.selection);
  }

  /** The checkbox is a pure toggle — it never opens the record. */
  function toggleRow(id: string) {
    selectByGesture(id, { ctrl: true, shift: false });
  }

  /** Select-all covers the rows ON SCREEN — post search/filter, every
   *  EXPANDED group. Ids selected but currently filtered out, or folded
   *  inside a collapsed group, are left alone. */
  function toggleSelectAllVisible() {
    const visible = new Set(visibleIds);
    if (allVisibleSelected) applySelection([...selected].filter((id) => !visible.has(id)));
    else applySelection([...new Set([...selected, ...visibleIds])]);
    selectionAnchor.current = null;
  }

  function clearSelection() {
    selectionAnchor.current = null;
    setBulkStatus(null);
    setBulkError(null);
    applySelection([]);
  }

  function handleRowClick(e: React.MouseEvent, item: T) {
    const ctrl = e.ctrlKey || e.metaKey;
    const shift = e.shiftKey;
    if (selectable && selectionMode === 'checkbox') {
      toggleRow(idOf(item));
      onRowClick?.(item);
      return;
    }
    if (!selectable || (!ctrl && !shift && onRowClick)) {
      // Plain click on a table whose rows open a record: open it, exactly as
      // before. The selection is not touched.
      onRowClick?.(item);
      return;
    }
    selectByGesture(idOf(item), { ctrl, shift });
  }

  // ─── Bulk operations ───

  const recordMenu = useRecordMenu();
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkError, setBulkError] = useState<string | null>(null);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  /** The inline "N archived · Undo" line. No toast primitive exists in the
   *  kit (adding one needs Josh's sign-off), so this IS the undo surface. */
  const [bulkStatus, setBulkStatus] = useState<{
    text: string;
    items: T[];
    onUndo?: (items: T[]) => Promise<void> | void;
  } | null>(null);
  const bulkStatusTimer = useRef<any>(null);

  useEffect(() => () => clearTimeout(bulkStatusTimer.current), []);

  function showBulkStatus(next: NonNullable<typeof bulkStatus>) {
    clearTimeout(bulkStatusTimer.current);
    setBulkStatus(next);
    bulkStatusTimer.current = setTimeout(() => setBulkStatus(null), 8000);
  }

  async function runBulk(
    items: T[],
    run: (items: T[]) => Promise<void> | void,
    done: { verb: string; onUndo?: (items: T[]) => Promise<void> | void }
  ) {
    setBulkError(null);
    setBulkStatus(null);
    setBulkBusy(true);
    try {
      await run(items);
      applySelection([]);
      selectionAnchor.current = null;
      showBulkStatus({
        text: `${items.length} ${done.verb}`,
        items,
        onUndo: done.onUndo,
      });
    } catch (err: any) {
      setBulkError(err?.message || `Could not ${done.verb.replace(/d$/, '')} those rows.`);
    } finally {
      setBulkBusy(false);
    }
  }

  /** One key, an ANY-of array, or `null` = ungated. */
  const permAllows = useCallback(
    (p: string | string[] | null | undefined) => {
      if (p == null) return true;
      return Array.isArray(p) ? hasAnyPermission(permissions, p) : hasPermission(permissions, p);
    },
    [permissions]
  );

  const canArchive = !!bulkActions?.archive && permAllows(bulkActions.archive.permission);
  const canDelete = !!bulkActions?.delete && permAllows(bulkActions.delete.permission);
  const canExport = !!bulkActions?.exportCsv && permAllows(bulkActions.exportCsv.permission);

  /** The text ONE cell contributes to a .csv file. Precedence: `getValue` →
   *  the rendered cell flattened to text → the raw stored field. Matches
   *  what the body shows, which `getColValue` alone does not. */
  const csvValue = useCallback((item: T, col: Column<T>): string | number => {
    if (col.csvValue) return col.csvValue(item);
    if (col.getValue) return col.getValue(item);
    if (col.render) return nodeToText(col.render(item)).replace(/\s+/g, ' ').trim();
    const v = (item as any)[col.key];
    return v == null ? '' : String(v);
  }, []);

  /** Exports the SELECTED rows — every one of them, including any the current
   *  search/filter hides, so the file always matches the "N selected" count.
   *  With nothing selected it exports what is on screen, which means the same
   *  set select-all would take: collapsed groups are NOT included. Current
   *  sort + group order, visible columns only, in their current order.
   *  Grouping adds a leading "Group" column. */
  function exportCsv() {
    const grouping = groupKey !== 'none';
    const groupOpt = grouping ? effectiveGroupOptions.find((g) => g.key === groupKey) : undefined;
    const headers = [...(grouping ? ['Group'] : []), ...orderedColumns.map((c) => c.label)];
    const rows: (string | number)[][] = [];
    const pushRow = (item: T, label: string) =>
      rows.push([...(grouping ? [label] : []), ...orderedColumns.map((c) => csvValue(item, c))]);
    if (selectedCount > 0) {
      for (const item of selectedItems)
        pushRow(item, groupOpt ? groupOpt.getValue(item) || 'Ungrouped' : '');
    } else {
      for (const g of grouped) {
        if (isGroupCollapsed(g.label)) continue;
        for (const item of g.items) pushRow(item, g.label);
      }
    }
    const name = bulkActions?.exportCsv?.filename
      ? bulkActions.exportCsv.filename
      : csvFilename(storageKey || 'table');
    downloadCsv(name, toCsv(headers, rows));
  }

  // ─── Column Drag Reorder ───
  const dragColRef = useRef<string | null>(null);
  const [dragOverCol, setDragOverCol] = useState<string | null>(null);

  function handleColDragStart(e: React.DragEvent, key: string) {
    dragColRef.current = key;
    e.dataTransfer.effectAllowed = 'move';
    try {
      const el = document.createElement('div');
      el.style.cssText = 'position:fixed;top:-9999px;opacity:0;pointer-events:none';
      document.body.appendChild(el);
      e.dataTransfer.setDragImage(el, 0, 0);
      requestAnimationFrame(() => {
        try {
          document.body.removeChild(el);
        } catch {}
      });
    } catch {}
  }

  function handleColDragOver(e: React.DragEvent, key: string) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverCol(key);
  }

  function handleColDrop(e: React.DragEvent, key: string) {
    e.preventDefault();
    setDragOverCol(null);
    const from = dragColRef.current;
    if (!from || from === key) return;
    setColumnOrder((prev) => {
      const arr = [...prev];
      const fi = arr.indexOf(from);
      const ti = arr.indexOf(key);
      if (fi === -1 || ti === -1) return arr;
      arr.splice(fi, 1);
      arr.splice(ti, 0, from);
      return arr;
    });
    // A deliberate user edit — stamped so it can outrank an OLDER firm
    // default on the next resolve (columnPrefs.ts ladder), but still loses
    // to a firm default published after this moment.
    if (storageKey) {
      const now = new Date().toISOString();
      try {
        localStorage.setItem(`dt_orderAt_${storageKey}`, now);
      } catch {}
      if (prefsAppliedRef.current) Prefs.set(`dt_orderAt_${storageKey}`, now);
    }
  }

  // ─── Column Resize ───
  // Track whether a drag/resize just ended — suppress next click to prevent sort/nav triggers
  const suppressClickRef = useRef(false);
  // Track mousedown target to prevent click-on-release from triggering wrong actions
  const mouseDownTargetRef = useRef<EventTarget | null>(null);
  // The scrolling data area — the resize guide line spans its full height.
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  const isResizing = useRef(false);
  // Which handle the pointer is over — only that one paints its hairline
  // accent, so the 14px hit area never shows as a 14px block.
  const [hoverResizeKey, setHoverResizeKey] = useState<string | null>(null);
  /** The guide line that runs the full height of the data area (header +
   *  body) down the dragged column's edge. Screen coords — it renders
   *  fixed, so a scrolled or zoomed table still lands on the edge. */
  const [resizeGuide, setResizeGuide] = useState<{
    x: number;
    top: number;
    height: number;
  } | null>(null);

  function handleResizeStart(e: React.MouseEvent, key: string) {
    e.preventDefault();
    e.stopPropagation();
    isResizing.current = true;
    const startX = e.clientX;
    const area = scrollAreaRef.current?.getBoundingClientRect();
    const guideTop = area ? area.top : 0;
    const guideHeight = area ? area.height : 0;
    const edgeOffset = e.currentTarget.getBoundingClientRect().right - startX;
    const startWidth = columnWidths[key] || 150;
    const minW = initialColumns.find((c) => c.key === key)?.minWidth || 50;
    // Stored widths are unscaled px; the drag itself moves in real screen
    // px, so the delta must be un-scaled before it's added back (DESIGN.md
    // §3.6 — nothing on screen is a fixed size at any dial/user setting).
    // renderScale (dial × this table's text slider) is the same factor the
    // width is rendered at, so a drag still tracks the mouse 1:1.
    const scaleAtStart = renderScale;
    let rafId = 0;
    let lastWidth = startWidth;
    setResizeGuide({ x: startX + edgeOffset, top: guideTop, height: guideHeight });
    const handleMove = (ev: MouseEvent) => {
      ev.preventDefault();
      const delta = (ev.clientX - startX) / scaleAtStart;
      lastWidth = Math.max(minW, startWidth + delta);
      if (rafId) cancelAnimationFrame(rafId);
      const clampedX = startX + (lastWidth - startWidth) * scaleAtStart + edgeOffset;
      rafId = requestAnimationFrame(() => {
        setColumnWidths((prev) => ({ ...prev, [key]: lastWidth }));
        setResizeGuide((g) => (g ? { ...g, x: clampedX } : g));
      });
    };
    const handleUp = () => {
      isResizing.current = false;
      if (rafId) cancelAnimationFrame(rafId);
      setColumnWidths((prev) => ({ ...prev, [key]: lastWidth }));
      setResizeGuide(null);
      suppressClickRef.current = true;
      setTimeout(() => {
        suppressClickRef.current = false;
      }, 0);
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
  }

  // ─── Sort Toggle ───
  function toggleSort(key: string) {
    if (suppressClickRef.current) return;
    if (sortKey === key) setSortAsc(!sortAsc);
    else {
      setSortKey(key);
      setSortAsc(true);
    }
  }
  const sortArrow = (key: string) => (sortKey === key ? (sortAsc ? ' \u25B2' : ' \u25BC') : '');

  // Track flat row index across groups during render
  let flatRowCounter = 0;

  // The firm-default column ladder has not resolved yet (first table shown
  // right after sign-in, before FirmPrefs/Prefs have loaded) — a skeleton,
  // never the code-default columns painted and then flipped once the real
  // resolution lands (table-firm-defaults plan's "no flicker" rule).
  if (storageKey && !colPrefsApplied) {
    return (
      <div role="status" aria-busy="true" style={{ padding: 'var(--space-4)' }}>
        <span className="sr-only">Loading table…</span>
        <div
          aria-hidden="true"
          style={{
            height: rem(280),
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--bd)',
            background: 'var(--sa)',
          }}
        />
      </div>
    );
  }

  // ─── Render ───
  return (
    <div
      style={{
        outline: 'none',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        flex: 1,
      }}
    >
      {/* Toolbar */}
      <div
        style={{
          display: 'flex',
          gap: 'var(--space-2)',
          alignItems: 'center',
          marginBottom: 'var(--space-3)',
          flexWrap: 'wrap',
          flexShrink: 0,
        }}
      >
        {!hideSearchBox && (
          <div
            style={{ position: 'relative', width: rem(200), flexShrink: 0, alignSelf: 'center' }}
          >
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={searchPlaceholder}
              style={{
                padding: `var(--space-1) ${rem(10)}`,
                paddingRight: search ? rem(24) : rem(10),
                minHeight: rem(32),
                display: 'block',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--bd)',
                background: 'var(--ib)',
                color: 'var(--tx)',
                fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                width: '100%',
                boxSizing: 'border-box',
              }}
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                style={{
                  position: 'absolute',
                  right: rem(6),
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: 'var(--tm)',
                  cursor: 'pointer',
                  fontSize: 'var(--font-size-md)',
                  lineHeight: 1,
                  padding: 0,
                }}
              >
                {'\u00d7'}
              </button>
            )}
          </div>
        )}

        {(() => {
          const controls = (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: rem(4) }}>
                <span style={toolLabel}>Sort:</span>
                <div style={{ minWidth: rem(140) }}>
                  <SearchableSelect
                    value={sortKey}
                    options={initialColumns
                      .filter((c) => c.sortable !== false)
                      .map((c) => ({ value: c.key, label: c.label }))}
                    onChange={(v) => setSortKey(v)}
                    searchPlaceholder="Search columns…"
                    clearable={false}
                    style={selectStyle}
                  />
                </div>
                <button onClick={() => setSortAsc(!sortAsc)} style={{ ...pillBtn, width: rem(24) }}>
                  {sortAsc ? '\u25B2' : '\u25BC'}
                </button>
              </div>

              {effectiveGroupOptions.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: rem(4) }}>
                  <span style={toolLabel}>Group:</span>
                  <div style={{ minWidth: rem(140) }}>
                    <SearchableSelect
                      value={groupKey}
                      options={[
                        { value: 'none', label: 'None' },
                        ...effectiveGroupOptions.map((g) => ({ value: g.key, label: g.label })),
                      ]}
                      onChange={(v) => setGroupKey(v || 'none')}
                      searchPlaceholder="Search columns…"
                      clearable={false}
                      style={selectStyle}
                    />
                  </div>
                </div>
              )}

              {/* Filter Builder — rules across any column, one modal */}
              {filterableColumns.length > 0 && (
                <div style={{ position: 'relative' }}>
                  <button
                    ref={filterBtnRef}
                    onClick={() => setFilterBuilderOpen(true)}
                    style={{
                      ...pillBtn,
                      padding: `var(--space-1) ${rem(10)}`,
                      gap: rem(4),
                      background: activeCount > 0 ? 'var(--ac)' : 'var(--sa)',
                      color: activeCount > 0 ? 'var(--color-text-inverse)' : 'var(--tm)',
                      borderRadius: rem(5),
                      fontWeight: 600,
                    }}
                    title="Filter rows"
                    aria-haspopup="dialog"
                  >
                    <Icon icon={Filter} em={1.15} />
                    Filter
                    {activeCount > 0 && (
                      <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700 }}>
                        ({activeCount})
                      </span>
                    )}
                  </button>
                </div>
              )}

              {/* Named saved views — surface derives from storageKey */}
              {viewSurface && !hideViewChrome && (
                <div style={{ display: 'flex', alignItems: 'center', gap: rem(4) }}>
                  <span style={toolLabel}>View:</span>
                  <div style={{ minWidth: rem(150) }} ref={viewSelectBtnRef}>
                    <SearchableSelect
                      value={activeViewId || ''}
                      options={[
                        { value: '', label: 'Current (unsaved)' },
                        ...savedViews.map((v) => ({
                          value: v.id,
                          label: `${v.name}${v.isDefault ? ' ★' : ''}${v.shared ? ' · shared' : ''}`,
                        })),
                      ]}
                      onChange={(v) => {
                        if (!v) {
                          setActiveViewId(null);
                          return;
                        }
                        const found = savedViews.find((sv) => sv.id === v);
                        if (found) applySavedView(found);
                      }}
                      searchPlaceholder="Search views…"
                      clearable={false}
                      style={selectStyle}
                    />
                  </div>
                  <button
                    ref={viewSaveBtnRef}
                    onClick={() => {
                      setViewSaveName(activeView?.name || '');
                      setViewSaveShared(!!activeView?.shared);
                      setViewSaveOpen((o) => !o);
                    }}
                    style={{ ...pillBtn, width: 'var(--space-6)', height: 'var(--space-6)' }}
                    title={activeView ? 'Update or save view' : 'Save current view'}
                  >
                    <Icon icon={Save} em={1.15} />
                  </button>
                  {activeView && (
                    <>
                      <button
                        onClick={() => setViewDefault(activeView.id)}
                        style={{
                          ...pillBtn,
                          width: 'var(--space-6)',
                          height: 'var(--space-6)',
                          color: activeView.isDefault ? 'var(--ac)' : 'var(--tm)',
                        }}
                        title={activeView.isDefault ? 'Default view' : 'Set as default'}
                      >
                        <Icon
                          icon={Star}
                          em={1.15}
                          fill={activeView.isDefault ? 'currentColor' : 'none'}
                        />
                      </button>
                      <button
                        onClick={() => setConfirmDeleteView(activeView)}
                        style={{ ...pillBtn, width: 'var(--space-6)', height: 'var(--space-6)' }}
                        title="Delete view"
                      >
                        <Icon icon={Trash2} em={1.15} />
                      </button>
                    </>
                  )}
                  {viewSaveOpen && viewSaveBtnRef.current && (
                    <AnchoredPopover
                      anchorRect={viewSaveBtnRef.current.getBoundingClientRect()}
                      width={240}
                      onClose={() => setViewSaveOpen(false)}
                      ignoreRef={viewSaveBtnRef}
                    >
                      <div
                        style={{
                          padding: rem(10),
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 'var(--space-2)',
                        }}
                      >
                        <input
                          value={viewSaveName}
                          onChange={(e) => setViewSaveName(e.target.value)}
                          placeholder="View name"
                          autoFocus
                          style={rangeInputStyle}
                        />
                        <label
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: rem(6),
                            fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                            color: 'var(--tx)',
                            cursor: 'pointer',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={viewSaveShared}
                            onChange={(e) => setViewSaveShared(e.target.checked)}
                          />
                          <Icon icon={Share2} em={0.9167} />
                          Share with firm
                        </label>
                        <div style={{ display: 'flex', gap: rem(6), justifyContent: 'flex-end' }}>
                          {activeView && (
                            <button
                              onClick={async () => {
                                await saveCurrentView(viewSaveName, viewSaveShared, undefined);
                                setViewSaveOpen(false);
                              }}
                              disabled={!viewSaveName.trim()}
                              style={{
                                ...pillBtn,
                                padding: `${rem(5)} ${rem(10)}`,
                                background: 'var(--sa)',
                              }}
                            >
                              Save as new
                            </button>
                          )}
                          <button
                            onClick={async () => {
                              await saveCurrentView(viewSaveName, viewSaveShared, activeView?.id);
                              setViewSaveOpen(false);
                            }}
                            disabled={!viewSaveName.trim()}
                            style={{
                              ...pillBtn,
                              padding: `${rem(5)} ${rem(10)}`,
                              background: 'var(--ac)',
                              color: 'var(--color-text-inverse)',
                              fontWeight: 600,
                            }}
                          >
                            {activeView ? 'Update' : 'Save'}
                          </button>
                        </div>
                      </div>
                    </AnchoredPopover>
                  )}
                </div>
              )}

              {/* Text size slider */}
              {!hideViewChrome && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: rem(4),
                    marginLeft: compactToolbar ? 0 : 'auto',
                  }}
                >
                  <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--tm)' }}>Aa</span>
                  <input
                    type="range"
                    min={75}
                    max={140}
                    step={5}
                    value={textScale}
                    onChange={(e) => setTextScale(Number(e.target.value))}
                    style={{ width: rem(60), cursor: 'pointer' }}
                    title={`Text size: ${textScale}%`}
                  />
                  <span
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--tm)',
                      minWidth: rem(30),
                      textAlign: 'right',
                    }}
                  >
                    {textScale}%
                  </span>
                </div>
              )}
            </>
          );

          if (!compactToolbar) return controls;

          const viewOptionsActive =
            activeCount > 0 ||
            groupKey !== 'none' ||
            textScale !== 100 ||
            sortKey !== defaultToolbarRef.current.sortKey ||
            sortAsc !== defaultToolbarRef.current.sortAsc;

          return (
            <div style={{ position: 'relative', marginLeft: 'auto' }}>
              <button
                ref={viewOptionsBtnRef}
                onClick={() => setViewOptionsOpen((o) => !o)}
                style={{ ...pillBtn, padding: `var(--space-1) ${rem(10)}`, gap: rem(4) }}
                aria-haspopup="true"
                aria-expanded={viewOptionsOpen}
                title={
                  viewOptionsActive
                    ? 'Sort, group, filter and view options — some are not at their default'
                    : 'Sort, group, filter and view options'
                }
              >
                <Icon icon={SlidersHorizontal} em={1.15} />
                View options
                {viewOptionsActive && (
                  <span
                    aria-hidden="true"
                    style={{
                      width: rem(6),
                      height: rem(6),
                      borderRadius: '50%',
                      background: 'var(--ac)',
                      flexShrink: 0,
                    }}
                  />
                )}
              </button>
              {viewOptionsOpen && viewOptionsBtnRef.current && (
                <AnchoredPopover
                  anchorRect={viewOptionsBtnRef.current.getBoundingClientRect()}
                  // AnchoredPopover's `width` is real screen px (it clamps
                  // against `window.innerWidth`), so it can't be a `rem()`
                  // CSS string — scaled by the same UI dial `rem()` itself
                  // rides (`uiEffectiveScale`, already tracked above) so
                  // this follows the app's scale instead of a bare raw px.
                  width={Math.round(260 * uiEffectiveScale)}
                  onClose={() => setViewOptionsOpen(false)}
                  ignoreRef={viewOptionsBtnRef}
                >
                  <div
                    style={{
                      padding: rem(10),
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: 'var(--space-2)',
                    }}
                  >
                    {controls}
                  </div>
                </AnchoredPopover>
              )}
            </div>
          );
        })()}

        {toolbarExtra}
      </div>

      {/* Selection + bulk-ops bar — its own line under the toolbar so a long
          toolbar never pushes the destructive button off screen. Collapses
          entirely (no reserved space) when nothing is selected — present
          only while rows are selected OR while an "N archived · Undo" line
          is up (a bulk run clears the selection, so without that clause the
          Undo would unmount the instant it appeared). */}
      {(selectedCount > 0 || !!bulkStatus) && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            flexWrap: 'wrap',
            padding: `var(--space-1) var(--space-2)`,
            marginBottom: 'var(--space-3)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--bd)',
            background: selectedCount > 0 ? 'rgba(var(--acr),0.08)' : 'var(--sa)',
            flexShrink: 0,
          }}
          role="group"
          aria-label="Bulk actions"
        >
          {selectedCount > 0 && (
            <>
              <span
                style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--tx)' }}
                aria-live="polite"
              >
                {selectedCount} selected
              </span>
              <Button variant="ghost" size="sm" onClick={clearSelection} disabled={bulkBusy}>
                Clear
              </Button>
            </>
          )}

          {selectedCount > 0 &&
            bulkActions?.extra?.(selectedItems, { count: selectedCount, clear: clearSelection })}

          {canExport && selectedCount > 0 && (
            <Button variant="outline" size="sm" onClick={exportCsv} disabled={bulkBusy}>
              <Icon icon={Download} em={1.15} />
              Export CSV
            </Button>
          )}

          {bulkStatus && (
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-1)',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--tm)',
              }}
              aria-live="polite"
            >
              {bulkStatus.text}
              {bulkStatus.onUndo && (
                <>
                  {'·'}
                  <Button
                    variant="link"
                    size="sm"
                    disabled={bulkBusy}
                    onClick={async () => {
                      const { items, onUndo } = bulkStatus;
                      setBulkStatus(null);
                      setBulkError(null);
                      setBulkBusy(true);
                      try {
                        await onUndo?.(items);
                      } catch (err: any) {
                        setBulkError(err?.message || 'Could not undo that.');
                      } finally {
                        setBulkBusy(false);
                      }
                    }}
                  >
                    Undo
                  </Button>
                </>
              )}
            </span>
          )}

          {bulkError && (
            <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--dg)' }} role="alert">
              {bulkError}
            </span>
          )}

          {/* Right-pinned "leave the list" group — Archive immediately left
              of Delete; the danger action stays last, separated from the
              safe ones only by the flex gap (§4). */}
          {selectedCount > 0 && (canArchive || canDelete) && (
            <div
              style={{
                marginLeft: 'auto',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
              }}
            >
              {canArchive && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={bulkBusy}
                  onClick={() =>
                    runBulk(selectedItems, bulkActions!.archive!.onArchive, {
                      verb: 'archived',
                      onUndo: bulkActions!.archive!.onUndo,
                    })
                  }
                >
                  <Icon icon={Archive} em={1.15} />
                  {bulkActions?.archive?.label || 'Archive'}
                </Button>
              )}

              {canDelete && (
                <Button
                  variant="destructive"
                  size="sm"
                  disabled={bulkBusy}
                  onClick={() => setConfirmBulkDelete(true)}
                >
                  <Icon icon={Trash2} em={1.15} />
                  {bulkActions?.delete?.label || 'Delete'}
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Active-filter chips — one per rule. The body reopens the builder,
          the × drops that rule. A group shows as one dashed read-only chip.
          Stacks with group + sort + search freely since they all read the
          same `filtered`. */}
      {activeRules.length > 0 && (
        <div
          style={{
            display: 'flex',
            gap: rem(6),
            flexWrap: 'wrap',
            marginBottom: rem(10),
            flexShrink: 0,
            alignItems: 'center',
          }}
        >
          {activeRules.map((node, i) => {
            const idx = filterState.rules.indexOf(node);
            const grouped = isFilterGroup(node);
            const text = grouped
              ? groupSummaryText(node as FilterGroup, fieldLabel, fieldValueLabel)
              : ruleValueText(node as FilterRule, (v) =>
                  fieldValueLabel((node as FilterRule).field, v)
                );
            const label = grouped
              ? (node as FilterGroup).match === 'any'
                ? 'Any of'
                : 'All of'
              : fieldLabel((node as FilterRule).field);
            return (
              <span
                key={`${grouped ? 'g' : 'r'}${idx}-${i}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: rem(4),
                  fontSize: 'var(--font-size-sm)',
                  padding: `${rem(2)} ${rem(4)} ${rem(2)} var(--space-2)`,
                  borderRadius: 'var(--radius-full)',
                  background: grouped ? 'var(--sa)' : 'rgba(var(--acr),0.10)',
                  color: 'var(--tx)',
                  border: grouped ? '1px dashed var(--bd)' : '1px solid rgba(var(--acr),0.25)',
                }}
              >
                <button
                  onClick={() => setFilterBuilderOpen(true)}
                  title={grouped ? `${label}: ${text}` : `Edit the ${label} filter`}
                  aria-label={grouped ? `${label} ${text}` : `Edit the ${label} filter`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: rem(4),
                    minHeight: rem(20),
                    padding: rem(2),
                    background: 'none',
                    border: 'none',
                    color: 'inherit',
                    font: 'inherit',
                    fontSize: 'var(--font-size-sm)',
                    cursor: 'pointer',
                  }}
                >
                  <strong style={{ fontWeight: 600 }}>{label}</strong>
                  {text}
                </button>
                <button
                  onClick={() =>
                    setFilterState({
                      ...filterState,
                      rules: filterState.rules.filter((_, ri) => ri !== idx),
                    })
                  }
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: 'var(--tm)',
                    display: 'flex',
                    alignItems: 'center',
                    padding: 'var(--space-1)',
                    borderRadius: 'var(--radius-full)',
                  }}
                  title={`Clear the ${label} filter`}
                  aria-label={`Clear the ${label} filter`}
                >
                  <Icon icon={X} size="md" />
                </button>
              </span>
            );
          })}
          <button
            onClick={() => setFilterState(EMPTY_FILTER_STATE)}
            style={{ ...pillBtn, fontSize: 'var(--font-size-xs)' }}
          >
            Clear all filters
          </button>
        </div>
      )}

      {/* Scrollable data area — text size is scoped HERE, not on the
          DataTable root, so the toolbar/chrome above (search, sort, group,
          filter, view, the Aa slider itself) stays at ambient (Interface
          size) scale and never moves under the cursor while dragging. Every
          token — font, space, radius, icon-size — scales together for the
          table body only (header, cells, chips, group headers, row-action
          icons). Ruling: Josh 2026-09-11. */}
      <div
        ref={scrollAreaRef}
        style={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          ...scaledTokens(textScale / 100),
          fontSize: 'var(--font-size-base)',
        }}
      >
        {/* Data */}
        {filtered.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: `${rem(50)} ${rem(20)}`,
              background: 'var(--sf)',
              borderRadius: 'var(--radius-lg)',
              border: '1px solid var(--bd)',
            }}
          >
            <div
              style={{
                fontSize: 'calc(var(--font-size-base) * 40 / 13)',
                opacity: 0.12,
                marginBottom: rem(10),
              }}
            >
              {emptyIcon}
            </div>
            <h3 style={{ fontSize: '1.1538em', margin: `0 0 ${rem(6)}`, fontWeight: 600 }}>
              {data.length === 0 ? emptyTitle : 'No matches'}
            </h3>
            <p style={{ fontSize: '0.9231em', color: 'var(--tm)', margin: 0 }}>
              {data.length === 0 ? emptyMessage : 'Try a different search.'}
            </p>
          </div>
        ) : (
          grouped.map((group, gi) => {
            if (gi === 0) flatRowCounter = 0;

            const isCollapsed = isGroupCollapsed(group.label);

            return (
              <div key={gi} style={{ marginBottom: groupKey !== 'none' ? rem(20) : 0 }}>
                {groupKey !== 'none' && group.label && (
                  <div
                    onClick={() =>
                      setCollapsedGroups((prev) => {
                        const next = new Set(prev);
                        if (next.has(group.label)) next.delete(group.label);
                        else next.add(group.label);
                        return next;
                      })
                    }
                    style={{
                      fontWeight: 700,
                      color: 'var(--tx)',
                      marginBottom: isCollapsed ? 0 : 'var(--space-2)',
                      paddingBottom: 'var(--space-1)',
                      borderBottom: '1px solid var(--bd)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--space-2)',
                      cursor: 'pointer',
                      userSelect: 'none',
                    }}
                  >
                    <span
                      style={{
                        fontSize: '0.8462em',
                        color: 'var(--tm)',
                        transition: 'transform .15s',
                        display: 'inline-block',
                        transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                      }}
                    >
                      {'\u25BE'}
                    </span>
                    {group.label}
                    <span
                      style={{
                        fontSize: 'max(0.7692em, 0.5625rem)',
                        color: 'var(--tm)',
                        fontWeight: 400,
                      }}
                    >
                      ({group.items.length})
                    </span>
                    {group.rollups.length > 0 && (
                      <span
                        style={{
                          display: 'flex',
                          gap: 'var(--space-2)',
                          marginLeft: 'auto',
                          fontSize: 'max(0.7692em, 0.5625rem)',
                          color: 'var(--tm)',
                          fontWeight: 500,
                        }}
                      >
                        {group.rollups.map((r) => (
                          <span key={r.label}>
                            {r.label}:{' '}
                            <strong style={{ color: 'var(--tx)' }}>{formatRollup(r.sum)}</strong>
                          </span>
                        ))}
                      </span>
                    )}
                  </div>
                )}

                {isCollapsed ? (
                  ((): null => {
                    flatRowCounter += group.items.length;
                    return null;
                  })()
                ) : (
                  <div
                    style={{
                      background: 'var(--sf)',
                      borderRadius: rem(8),
                      border: '1px solid var(--bd)',
                      marginBottom: 'var(--space-1)',
                    }}
                  >
                    <table
                      style={{
                        width: Math.max(
                          orderedColumns.reduce(
                            (s, c) => s + (columnWidths[c.key] || 150) * renderScale,
                            0
                          ) + (selectable ? SELECT_COL_PX * renderScale : 0),
                          0
                        ),
                        borderCollapse: 'collapse',
                        tableLayout: 'fixed',
                      }}
                    >
                      <colgroup>
                        {/* Fixed, never resizable/reorderable/hideable, never
                            exported — it is chrome, not data. */}
                        {selectable && <col style={{ width: SELECT_COL_PX * renderScale }} />}
                        {orderedColumns.map((col) => (
                          <col
                            key={col.key}
                            style={{ width: (columnWidths[col.key] || 150) * renderScale }}
                          />
                        ))}
                      </colgroup>
                      <thead>
                        <tr>
                          {selectable && (
                            <th
                              style={{
                                ...thStyle,
                                padding: `var(--space-1) 0 var(--space-1) var(--space-2)`,
                                borderRight: '1px solid var(--bd)',
                              }}
                            >
                              {groupKey !== 'none' ? (
                                (() => {
                                  const gIds = group.items.map(idOf);
                                  const gAll = allRowsSelected(gIds, selected);
                                  const gSome = !gAll && gIds.some((id) => selected.has(id));
                                  return (
                                    <Checkbox
                                      checked={gAll}
                                      indeterminate={gSome}
                                      onCheckedChange={() => {
                                        const ids = new Set(gIds);
                                        if (gAll)
                                          applySelection(
                                            [...selected].filter((id) => !ids.has(id))
                                          );
                                        else applySelection([...new Set([...selected, ...gIds])]);
                                      }}
                                      aria-label={`Select all in ${group.label}`}
                                    />
                                  );
                                })()
                              ) : (
                                <Checkbox
                                  checked={allVisibleSelected}
                                  indeterminate={someVisibleSelected}
                                  onCheckedChange={() => toggleSelectAllVisible()}
                                  aria-label="Select all"
                                />
                              )}
                            </th>
                          )}
                          {orderedColumns.map((col, colIdx) => (
                            <th
                              key={col.key}
                              draggable={!isResizing.current}
                              onDragStart={(e) => {
                                if (isResizing.current) {
                                  e.preventDefault();
                                  return;
                                }
                                handleColDragStart(e, col.key);
                              }}
                              onDragOver={(e) => handleColDragOver(e, col.key)}
                              onDragLeave={() => setDragOverCol(null)}
                              onDrop={(e) => handleColDrop(e, col.key)}
                              onMouseDown={(e) => {
                                mouseDownTargetRef.current = e.currentTarget;
                              }}
                              onClick={(e) => {
                                if (
                                  mouseDownTargetRef.current === e.currentTarget &&
                                  col.sortable !== false
                                )
                                  toggleSort(col.key);
                                mouseDownTargetRef.current = null;
                              }}
                              onContextMenu={(e) => {
                                e.preventDefault();
                                setColMenuPos({ x: e.clientX, y: e.clientY });
                              }}
                              style={{
                                ...thStyle,
                                fontSize: 'max(0.7692em, 0.5625rem)',
                                cursor: col.sortable !== false ? 'pointer' : 'default',
                                background:
                                  dragOverCol === col.key ? 'rgba(var(--acr),0.12)' : 'var(--sa)',
                                userSelect: 'none',
                                // Subtle divider in the HEADER only — the body
                                // stays line-free (D2b). Never on the last
                                // header cell, which would draw a stray edge.
                                borderRight:
                                  colIdx < orderedColumns.length - 1
                                    ? '1px solid var(--bd)'
                                    : undefined,
                              }}
                            >
                              <span
                                style={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {col.label}
                                {col.sortable !== false ? sortArrow(col.key) : ''}
                              </span>
                              {/* Resize handle. The grab area straddles the
                                  column edge and runs the full header height
                                  (§4 Fitts — generous hit area via padding,
                                  not visual bloat); only a hairline paints. */}
                              <div
                                onMouseDown={(e) => handleResizeStart(e, col.key)}
                                onClick={(e) => e.stopPropagation()}
                                onDragStart={(e) => e.preventDefault()}
                                onMouseEnter={() => setHoverResizeKey(col.key)}
                                onMouseLeave={() => setHoverResizeKey(null)}
                                role="separator"
                                aria-label={`Resize ${col.label} column`}
                                style={{
                                  position: 'absolute',
                                  right: rem(-7),
                                  top: 0,
                                  bottom: 0,
                                  width: rem(14),
                                  display: 'flex',
                                  justifyContent: 'center',
                                  cursor: 'col-resize',
                                  background: 'transparent',
                                  zIndex: 1,
                                }}
                              >
                                <span
                                  style={{
                                    width: '2px',
                                    alignSelf: 'stretch',
                                    background:
                                      hoverResizeKey === col.key ? 'var(--ac)' : 'transparent',
                                  }}
                                />
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {group.items.map((item) => {
                          const rowId = String((item as any)[keyField]);
                          const rowIdx = flatRowCounter++;
                          const isSelected = selectable && selected.has(rowId);
                          return (
                            <tr
                              key={rowId}
                              data-flat-idx={rowIdx}
                              draggable={!!onRowDragStart}
                              onDragStart={
                                onRowDragStart ? (e) => onRowDragStart(item, e) : undefined
                              }
                              onDragEnd={onRowDragEnd ? (e) => onRowDragEnd(item, e) : undefined}
                              onContextMenu={
                                onRowContextMenu
                                  ? (e) => {
                                      e.preventDefault();
                                      onRowContextMenu(item, e);
                                    }
                                  : rowHref
                                    ? (e) => {
                                        recordMenu.show(e, rowHref(item), () =>
                                          handleRowClick(PLAIN_CLICK, item)
                                        );
                                      }
                                    : undefined
                              }
                              onMouseDown={(e) => {
                                mouseDownTargetRef.current = e.currentTarget;
                              }}
                              onClick={(e) => {
                                // The guard survives: a click that began on a
                                // resize handle or a drag never counts as a row
                                // click.
                                if (mouseDownTargetRef.current === e.currentTarget)
                                  handleRowClick(e, item);
                                mouseDownTargetRef.current = null;
                              }}
                              style={{
                                cursor: onRowClick || selectable ? 'pointer' : 'default',
                                transition: 'background .1s',
                                background: isSelected ? SELECTED_ROW_BG : undefined,
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = isSelected
                                  ? SELECTED_ROW_HOVER_BG
                                  : 'var(--sa)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = isSelected
                                  ? SELECTED_ROW_BG
                                  : '';
                              }}
                            >
                              {selectable && (
                                <td
                                  style={{
                                    ...tdStyle,
                                    padding: `var(--space-1) 0 var(--space-1) var(--space-2)`,
                                  }}
                                  onMouseDown={(e) => e.stopPropagation()}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Checkbox
                                    checked={isSelected}
                                    onCheckedChange={() => toggleRow(rowId)}
                                    aria-label="Select row"
                                  />
                                </td>
                              )}
                              {orderedColumns.map((col, colIdx) => {
                                const cell = col.render
                                  ? col.render(item)
                                  : String(getColValue(item, col) || '\u2013');
                                const href = colIdx === 0 ? rowHref?.(item) : undefined;
                                return (
                                  <td
                                    key={col.key}
                                    style={{
                                      ...tdStyle,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      verticalAlign: 'top',
                                    }}
                                  >
                                    {href ? (
                                      <RecordLink
                                        href={href}
                                        bubble
                                        deferShift={selectable}
                                        draggable={false}
                                        onOpen={() => handleRowClick(PLAIN_CLICK, item)}
                                        style={{
                                          display: 'block',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                        }}
                                      >
                                        {cell}
                                      </RecordLink>
                                    ) : (
                                      cell
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
      {/* end scrollable data area */}

      {recordMenu.menu}
      {/* Column visibility context menu */}
      {colMenuPos && (
        <div
          {...appLayerProps}
          style={{ position: 'fixed', inset: 0, zIndex: 9999 }}
          onClick={() => setColMenuPos(null)}
          onContextMenu={(e) => {
            e.preventDefault();
            setColMenuPos(null);
          }}
        >
          <div
            {...appLayerProps}
            style={{
              position: 'absolute',
              left: colMenuPos.x,
              top: colMenuPos.y,
              background: 'var(--sf)',
              border: '1px solid var(--bd)',
              borderRadius: rem(8),
              boxShadow: '0 8px 24px rgba(0,0,0,.18)',
              padding: `${rem(6)} 0`,
              minWidth: rem(180),
              zIndex: 10000,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                padding: `var(--space-1) ${rem(14)} ${rem(6)}`,
                fontSize: 'var(--font-size-xs)',
                fontWeight: 700,
                color: 'var(--tm)',
                textTransform: 'uppercase',
                letterSpacing: '.04em',
              }}
            >
              Columns
            </div>
            {allOrderedColumns.map((col) => (
              <button
                key={col.key}
                onClick={() => {
                  setHiddenColumns((prev) => {
                    const next = new Set(prev);
                    if (next.has(col.key)) next.delete(col.key);
                    else next.add(col.key);
                    return next;
                  });
                  // A deliberate user edit — see the drag-reorder handler's
                  // comment for why this timestamp matters.
                  if (storageKey) {
                    const now = new Date().toISOString();
                    try {
                      localStorage.setItem(`dt_hiddenAt_${storageKey}`, now);
                    } catch {}
                    if (prefsAppliedRef.current) Prefs.set(`dt_hiddenAt_${storageKey}`, now);
                  }
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                  width: '100%',
                  textAlign: 'left',
                  padding: `${rem(6)} ${rem(14)}`,
                  fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                  fontWeight: 500,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--tx)',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--sa)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
              >
                <span
                  style={{
                    width: 'var(--space-4)',
                    textAlign: 'center',
                    fontSize: 'var(--font-size-base)',
                    color: hiddenColumns.has(col.key) ? 'var(--bd)' : 'var(--ac)',
                  }}
                >
                  {hiddenColumns.has(col.key) ? '\u2610' : '\u2611'}
                </span>
                {col.label || col.key}
              </button>
            ))}
            {storageKey && (
              <>
                <div
                  role="separator"
                  style={{ borderTop: '1px solid var(--bd)', margin: `${rem(6)} 0` }}
                />
                <button
                  onClick={() => {
                    handleResetColumnsToDefault();
                    setColMenuPos(null);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    minHeight: rem(32),
                    textAlign: 'left',
                    padding: `${rem(6)} ${rem(14)}`,
                    fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                    fontWeight: 500,
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: 'var(--tx)',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--sa)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                >
                  Reset to default
                </button>
                {canSetFirmColumnDefault && (
                  <button
                    disabled={colActionBusy}
                    onClick={() => {
                      void handleSetFirmColumnDefault();
                      setColMenuPos(null);
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                      minHeight: rem(32),
                      textAlign: 'left',
                      padding: `${rem(6)} ${rem(14)}`,
                      fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                      fontWeight: 500,
                      background: 'none',
                      border: 'none',
                      cursor: colActionBusy ? 'default' : 'pointer',
                      color: 'var(--tx)',
                      opacity: colActionBusy ? 0.6 : 1,
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--sa)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                  >
                    Set as firm default
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* "Reset to default" / "Set as firm default" result — no toast
          primitive exists in the kit (same gap noted at bulkStatus above),
          so this is a small self-dismissing status line, portalled so it
          survives the column menu closing. */}
      {colActionStatus &&
        createPortal(
          <div
            role="status"
            aria-live="polite"
            style={{
              position: 'fixed',
              left: '50%',
              bottom: rem(24),
              transform: 'translateX(-50%)',
              background: 'var(--sf)',
              border: `1px solid ${colActionStatus.error ? 'var(--dg)' : 'var(--bd)'}`,
              borderRadius: rem(8),
              boxShadow: '0 8px 24px rgba(0,0,0,.18)',
              padding: `${rem(8)} ${rem(14)}`,
              fontSize: 'var(--font-size-sm)',
              color: colActionStatus.error ? 'var(--dg)' : 'var(--tx)',
              zIndex: 2147483000,
            }}
          >
            {colActionStatus.text}
          </div>,
          document.body
        )}

      {/* Column-resize guide — a subtle line down the dragged column's edge,
          the full height of the data area (D2b). Fixed + pointer-events
          none so it never blocks the drag or gets clipped by the scroller. */}
      {resizeGuide &&
        createPortal(
          <div
            aria-hidden
            style={{
              position: 'fixed',
              left: resizeGuide.x,
              top: resizeGuide.top,
              height: resizeGuide.height,
              width: '1px',
              background: 'var(--ac)',
              opacity: 0.55,
              pointerEvents: 'none',
              zIndex: 2147483000,
            }}
          />,
          document.body
        )}

      {/* Bulk delete — kit confirm, destructive (§4) */}
      <ConfirmDialog
        open={confirmBulkDelete}
        onOpenChange={(o) => !o && setConfirmBulkDelete(false)}
        title={
          bulkActions?.delete?.confirmTitle ||
          `Delete ${selectedCount} ${bulkActions?.delete?.confirmNoun || 'item'}${selectedCount === 1 ? '' : 's'}?`
        }
        description={
          bulkActions?.delete?.confirmDescription?.(selectedCount) ||
          (bulkActions?.delete?.onUndo
            ? 'You can undo this straight after.'
            : "This can't be undone.")
        }
        confirmLabel={bulkActions?.delete?.label || 'Delete'}
        destructive
        busy={bulkBusy}
        onConfirm={async () => {
          if (!bulkActions?.delete) return;
          await runBulk(selectedItems, bulkActions.delete.onDelete, {
            verb: 'deleted',
            onUndo: bulkActions.delete.onUndo,
          });
          setConfirmBulkDelete(false);
        }}
      />

      {/* Delete saved view — kit confirm, never a native confirm() */}
      <ConfirmDialog
        open={!!confirmDeleteView}
        onOpenChange={(o) => !o && setConfirmDeleteView(null)}
        title="Delete saved view?"
        description={
          confirmDeleteView
            ? `"${confirmDeleteView.name}" will be removed for ${confirmDeleteView.shared ? 'everyone in the firm' : 'you'}. This can't be undone.`
            : ''
        }
        confirmLabel="Delete view"
        destructive
        onConfirm={async () => {
          if (!confirmDeleteView) return;
          await deleteSavedView(confirmDeleteView.id);
          setConfirmDeleteView(null);
        }}
      />

      {/* Filter Builder — draft edits, nothing lands until Apply */}
      {filterableColumns.length > 0 && (
        <FilterBuilder
          open={filterBuilderOpen}
          onClose={() => {
            setFilterBuilderOpen(false);
            filterBtnRef.current?.focus();
          }}
          fields={filterFields}
          value={filterState}
          onApply={setFilterState}
          totalRows={data.length}
          countMatches={countMatches}
          onSaveAsView={
            viewSurface
              ? (state) => {
                  // Reuses the toolbar's existing save-view flow verbatim: the
                  // draft lands first, then the name/share popover opens.
                  setFilterState(state);
                  setViewSaveName(activeView?.name || '');
                  setViewSaveShared(!!activeView?.shared);
                  setViewSaveOpen(true);
                }
              : undefined
          }
        />
      )}
    </div>
  );
}

// ─── Styles ───

/** Checkbox column — fixed, unscaled px (multiplied by renderScale like
 *  every other authored width, so it follows the dial and the Aa slider). */
const SELECT_COL_PX = 40;

/** Selection must be VISIBLE and distinct from hover (D2c). */
const SELECTED_ROW_BG = 'rgba(var(--acr),0.12)';
const SELECTED_ROW_HOVER_BG = 'rgba(var(--acr),0.18)';

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: 'var(--space-2) var(--space-3)',
  fontWeight: 700,
  color: 'var(--tm)',
  textTransform: 'uppercase',
  letterSpacing: '.04em',
  borderBottom: '1px solid var(--bd)',
  background: 'var(--sa)',
  position: 'sticky',
  top: 0,
  zIndex: 2,
};

const tdStyle: React.CSSProperties = {
  padding: 'var(--space-2) var(--space-3)',
  borderBottom: '1px solid var(--bd)',
};

const selectStyle: React.CSSProperties = {
  padding: `var(--space-1) var(--space-2)`,
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--bd)',
  background: 'var(--ib)',
  color: 'var(--tx)',
  fontSize: 'var(--font-size-sm)',
  cursor: 'pointer',
};

// Filter-bar / saved-view popover inputs (search box, text-contains,
// number/date range, view-name field) — one shared style, tokens only.
const rangeInputStyle: React.CSSProperties = {
  flex: 1,
  width: '100%',
  padding: `${rem(5)} var(--space-2)`,
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--bd)',
  background: 'var(--ib)',
  color: 'var(--tx)',
  fontSize: 'calc(var(--font-size-base) * 12 / 13)',
  outline: 'none',
  boxSizing: 'border-box',
};

const pillBtn: React.CSSProperties = {
  padding: `var(--space-1) ${rem(6)}`,
  borderRadius: 'var(--radius-sm)',
  border: 'none',
  background: 'transparent',
  color: 'var(--tm)',
  cursor: 'pointer',
  fontSize: 'var(--font-size-sm)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const toolLabel: React.CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  color: 'var(--tm)',
  fontWeight: 600,
};
